import { defineStore } from "pinia";
import axios, { AxiosRequestConfig } from 'axios';

import DeviceType from '@/types/device/DeviceType'
import LocationType from "@/types/location/LocationType";
import Location from "@/types/location/Location"
import NewLocationRequest from "@/types/api/post/NewLocationRequest";
import Branch from "@/types/location/Branch";
import Device from "@/types/device/Device";
import DevicesPerContractExport from "@/types/device/DevicesPerContractExport";
import NewNetworkRequest from "@/types/api/post/NewNetworkRequest";
import Network from "@/types/network/Network";
import { TOAST_SUCCESS_OPTIONS } from "@/utils/globals";
import NewDeviceRequest from "@/types/api/post/NewDeviceRequest";
import NewLocationBranchRequest from "@/types/api/post/NewLocationBranchRequest";
import Procedure from "@/types/procedure/Procedure";
import ProcedureDocumentation from "@/types/procedure/ProcedureDocumentation";
import NewLocationTypeRequest from "@/types/api/post/NewLocationTypeRequest";
import NewDeviceTypeRequest from "@/types/api/post/NewDeviceTypeRequest";
import ID from "@/types/utils/ID";
import PatchLocationRequest from "@/types/api/patch/PatchLocationRequest";
import PatchNetworkRequest from "@/types/api/patch/PatchNetworkRequest";
import PatchLocationTypeRequest from "@/types/api/patch/PatchLocationTypeRequest";
import PatchLocationBranchRequest from "@/types/api/patch/PatchLocationBranchRequest";
import PatchDeviceRequest from "@/types/api/patch/PatchDeviceRequest";
import PatchDeviceTypeRequest from "@/types/api/patch/PatchDeviceTypeRequest";
import VPNStatus from "@/types/vpn/VPNStatus";
import GeocodeSearchResponse from "@/types/geocode/GeocodeSearchResponse";
import Message from "@/types/message/Message";
import type { SystemLog } from "@/types/logs/SystemLog";
import TopdeskTicket from "@/types/topdesk/TopdeskTicket";
import TopdeskChange from "@/types/topdesk/TopdeskChange";
import TopdeskFilters from "@/types/topdesk/TopdeskFilters";
import SupportDeskTicketTab from "@/types/topdesk/SupportDeskTicketTab";
import TopdeskTicketDetail from "@/types/topdesk/TopdeskTicketDetail";
import TopdeskAttachment from "@/types/topdesk/TopdeskAttachment";
import Organization from "@/types/organization/Organization";
import OrganizationSetting from "@/types/organization/OrganizationSetting";
import PatchOrganizationRequest from "@/types/api/patch/PatchOrganizationRequest";
import PatchOrganizationIntegrationsRequest from "@/types/api/patch/PatchOrganizationIntegrationsRequest";
import NetworkRange from "@/types/NetworkRange";
import VLANRule from "@/types/VLANRule";
import VLANRuleAction from "@/types/VLANRuleAction";
import Country from "@/types/Country";
import IPAM from "@/types/IPAM";
import SwitchportTemplates from "@/types/meraki/SwitchportsTemplates";
import NetworkRangeRequest from "@/types/NetworkRangeRequest";
import VLANRuleRequest from "@/types/VLANRuleRequest";
import VLANRuleActionRequest from "@/types/VLANRuleActionRequest";
import NetworkSize from "@/types/NetworkSize";
import { apiErrorMessage, defaultPagination, downloadFile, getOrFile, getResponseErrors, signDictWithElevatedPermissions, signWithElevatedPermissions, sleep, toastError, toastGeneral, toastSuccess } from "@/utils/helpers";
import OrganizationSecret from "@/types/organization/OrganizationSecret";
import User from "@/types/users/User";
import Invitation from "@/types/users/Invitation";
import PatchUserRequest from "@/types/api/patch/PatchUserRequest";
import NewInvitationRequest from "@/types/api/post/NewInvitationRequest";
import ConnectedClient from "@/types/connectedClients/ConnectedClient";
import speedtest from "@/types/speedtest/speedtest";
import servicedeskIssuers from "@/utils/servicedeskIssuers";
import Role from "@/types/roles/role";
import UploadedFile from "@/types/uploadedFiles/uploadedFile";
import _ from 'lodash'
import AboutApplication from "@/types/about/AboutApplication";
import Version from "@/types/about/Version";
import PatchRoleRequest from "@/types/api/patch/PatchRoleRequest";
import NewRoleRequest from "@/types/api/post/NewRoleRequest";
import PasswordResetRequest from "@/types/api/post/PasswordResetRequest";
import Icon from '@/types/ui/Icon'
import NewUserRequest from "@/types/api/post/NewUserRequest";
import GeocodeCountry from "@/types/geocode/GeocodeCountry";
import GeoCodeTimezone from "@/types/geocode/GeocodeTimezone";
import CameraAccessRequest from "@/types/api/post/CameraAccessRequest";
import TroubledInternetConnection from "@/types/internetConnections/TroubledInternetConnection";
import FailedInternetConnection from "@/types/internetConnections/FailedInternetConnection";
import RMSCellularBackup from "@/types/rms/RMSCellularBackup";
import LocationTypeTransferRequest from "@/types/api/post/LocationTypeTransferRequest";
import LocationBranchTransferRequest from "@/types/api/post/LocationBranchTransferRequest";
import OrganizationModulePermission from "@/types/organization/OrganizationModulePermission";
import { bundleShortcuts } from "@/utils/permissionShortcuts";
import { hasAnyUserPermissions, hasOrganizationModulePermission, hasOrganizationModulePermissions, hasUserPermissions } from "@/utils/permissions";
import Paginated from "@/types/api/Paginated";
import SubMessage from "@/types/message/SubMessage";
import Integration from "@/types/organization/Integration";
import LastSync from "@/types/LastSync";
import MerakiCellularBackup from "@/types/meraki/CellularBackup";
import CameraSnapshot from "@/types/meraki/CameraSnapshot";
import DeviceSerial from "@/types/meraki/DeviceSerial";
import Subnet from "@/types/ui/Subnet";
import LocationDocumentation from "@/types/location/LocationDocumentation";
import LocationStatus from "@/types/location/LocationStatus";
import MerakiSettings from "@/types/meraki/MerakiSettings";
import PatchMerakiSettingsRequest from "@/types/api/patch/PatchMerakiSettingsRequest";
import ChangePasswordRequest from "@/types/api/patch/ChangePasswordRequest";
import PatchUserRoles from "@/types/api/patch/PatchUserRoles";
import { useAuthStore } from "./AuthStore";
import VLAN from "@/types/VLAN";
import APIKey from "@/types/api-keys/APIKey";
import NewAPIKeyRequest from "@/types/api/post/NewAPIKeyRequest";
import AddDeviceToNetworkRequest from "@/types/api/post/AddDeviceToNetworkRequest";
import SwapDeviceRequest from "@/types/api/post/SwapDeviceRequest";
import Template from "@/types/template/template";
import NetworkNamingConvention from "@/types/meraki/NetworkNamingConvention";
import NetworkNamingStrategyRequest from "@/types/api/patch/NetworkNamingStrategyRequest";
import VLANPrecalculation from "@/types/meraki/VLANPrecalculation";
import MagicLink from "@/types/security/MagicLink";
import TemplateRule from "@/types/template/TemplateRule";
import OrganizationStrategy from "@/types/organization/OrganizationStrategy";
import LocationNamingConvention from "@/types/location/LocationNamingConvention";
import LocationNamingStrategyRequest from "@/types/api/patch/LocationNamingStrategyRequest";
import { useUIStore } from "./UIStore";
import ConnectMicrosoftIntegrationRequest from "@/types/api/post/ConnectMicrosoftIntegrationRequest";
import VPNPeerName from "@/types/vpn/VPNPeerName";
import PatchSimNoteRequest from "@/types/api/patch/PatchSimNoteRequest";
import SimCard from "@/types/sim/SimCard";
import VerkadaCamera from "@/types/verkada/VerkadaCamera";
import Versions from "@/types/about/Versions";
import VerkadaACU from "@/types/verkada/VerkadaACU";
import CachedPaginated from "@/types/api/CachedPaginated";
import VerkadaSite from "@/types/verkada/VerkadaSite";
import VerkadaDoor from "@/types/verkada/VerkadaDoor";
import SwitchPort from "@/types/meraki/SwitchPort";
import WyebotSite from "@/types/wyebot/WyebotSite";
import WyebotSensor from "@/types/wyebot/WyebotSensor";
import WyebotNetworkTestProfile from "@/types/wyebot/WyebotNetworkTestProfile";
import NewWyebotNetworkTestRequest from "@/types/api/post/NewWyebotNetworkTestRequest";
import WyebotNetworkTest from "@/types/wyebot/WyebotNetworkTest";
import GranularPermissionsConfig from "@/types/granularPermissions/GranularPermissionsConfig";
import LocationGranularPermissions from "@/types/granularPermissions/locations/LocationGranularPermissions";
import PatchLocationGranularPermissionsRequest from "@/types/api/patch/gp/PatchLocationGranularPermissionsRequest";
import SwitchportStatus from "@/types/meraki/SwitchportStatus";
import MXPortTemplates from "@/types/meraki/MXPortTemplates";
import SwitchportStatuses from "@/types/meraki/SwitchportStatuses";
import TenantSwitcherItem from "@/types/tenant/TenantSwitcherItem";
import { env } from "@/config/env";

// router.beforeEach calls getOrganizationSettings() on every navigation;
// while it's failing, rapid navigations would otherwise each raise their
// own interceptor toast. sessionStorage (not a module variable) is used so
// this also covers a hard reload, not just client-side route changes.
const ORG_SETTINGS_FAILURE_KEY = 'orgSettingsFailedAt'
const ORG_SETTINGS_FAILURE_TTL_MS = 5_000
const recentlyFailedOrgSettings = () => {
  const at = Number(sessionStorage.getItem(ORG_SETTINGS_FAILURE_KEY))
  return at > 0 && Date.now() - at < ORG_SETTINGS_FAILURE_TTL_MS
}

/** How often the Devices per Contract export status is polled. */
export const DEVICES_PER_CONTRACT_POLL_INTERVAL_MS = 3000
/** How long the devices page keeps polling before it leaves the user to the notification. */
export const DEVICES_PER_CONTRACT_POLL_TIMEOUT_MS = 15 * 60 * 1000

const saveBlobAs = (data: Blob, fileName: string) => {
  const href = URL.createObjectURL(data)
  const link = document.createElement("a")
  link.href = href
  link.setAttribute("download", fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(href)
}

export const useAPIStore = defineStore('apiStore', {
  state: () => ({
    messages: [],
    messagesNext: null as string | null,
    messagesCount: 0,
    messagesPageSize: 25,
    unreadMessagesCount: 0,
    tickets: {
      all: [],
      veducon: [],
      cgi: [],
      gtt: [],
      // Tickets for the configurable requester-suffix tab (e.g. Pollux).
      // When suffix-tabs become a list, key these per configured tab instead.
      custom: [],
    },
    supportDeskTicketTab: {
      id: "",
      label: "",
      email_suffix: "",
    },
    detailLocation: {},
    detailLocationDocumentation: {},
    detailProcedure: {},
    detailProcedureDocumentation: {},
    detailNetwork: {},
    detailRole: {},
    detailUser: {},
    profile: {
      id: "",
      first_name: "",
      last_name: "",
      profile_image: "",
      job_title: "",
      email: "",
      is_loaded: false,
      microsoft_sso_user: false,
    },
    userData: null,
    userPermissions: [],
    // Cached list of the current user's other tenants. Prefetched at app
    // load so the "Switch tenant" dropdown opens instantly. `null` means
    // not yet loaded; `[]` means loaded with no other tenants.
    myTenants: null as null | TenantSwitcherItem[],
    openTicketsCount: null,
    completedTicketsCount: 0,
    changes: [],
    organizationLoaded: false,
    granularPermissionsConfig: null,
    granularPermissionsRules: {} as Record<string, any>,
    organization: {
      id: "",
      name: "",
      description: "",
      managed_services_name: "",
      onboarding_complete: false,
      large_logo: "",
      small_logo: "",
      login_logo: null,
      login_banner: null,
      favicon_logo: null,
      prefer_login_mode: null,
      has_microsoft_login: false,
    },
    organizationStrategy: {
      location_branch_code_minlen: 0,
      location_branch_code_maxlen: 0,
      location_type_code_minlen: 0,
      location_type_code_maxlen: 0,
      can_delete_networks: false,
      can_edit_location_details: false,
      network_naming_strategy: null,
      location_naming_strategy: null,
      default_product_types: [],
      default_network_tags: [],
    },
    organizationModulePermissions: [],
    organizationSettings: {
      onboarding_complete: false,
    },
    microsoftSSOConfigured: false,
    worldMapLocations: [],
    imageSnapshot: null,
    wyebotCloudEnvironment: null,
    locationTypes: [] as LocationType[],
    locationBranches: [] as Branch[],
    countries: [] as any[],
  }),
  actions: {
    async getIP() {
      const { data } = await axios.get(`ip/`)
      return data.ip as string
    },

    async getOrganizationStrategy() {
      const { data } = await axios.get('strategy/')

      this.organizationStrategy = data

      return this.organizationStrategy as OrganizationStrategy
    },

    async getPrecalculatedTemplateVLANAssignment(locationID: ID, templateID: ID) {
      // TODO: add permissions
      const { data } = await axios.get('calculate-template-vlans/', {
        params: {
          location: locationID,
          template: templateID,
        }
      })
      return data as VLANPrecalculation[]
    },

    async getGranularPermissions() {
      if (!hasOrganizationModulePermissions(['GRANULAR_PERMISSIONS'])) {
        this.granularPermissionsConfig = null
        return null
      }

      try{
        const { data } = await axios.get('granular-permissions/')
        this.granularPermissionsConfig = data
      }catch{
        this.granularPermissionsConfig = null
      }

      return this.granularPermissionsConfig as GranularPermissionsConfig
    },
    async getLocationGranularPermissions() {
      if (!hasOrganizationModulePermissions(['GRANULAR_PERMISSIONS'])) {
        return null as any
      }

      const { data } = await axios.get('granular-permissions/locations/')
      return data as LocationGranularPermissions
    },
    async fetchGranularPermissions(moduleKey: string, getFn: () => Promise<any>, bypassCache = false) {
      if (!hasOrganizationModulePermissions(['GRANULAR_PERMISSIONS'])) {
        const mockedData = { enabled: false, moduleKey, default: "allow all actions", rules: [] };
        this.granularPermissionsRules[moduleKey] = mockedData;
        return mockedData;
      }

      try {
        if (!bypassCache && this.granularPermissionsRules[moduleKey]) {
          return this.granularPermissionsRules[moduleKey];
        }
        const data = await getFn();
        this.granularPermissionsRules[moduleKey] = data;
        return data;
      } catch (e) {
        console.error(`[APIStore] Failed to fetch GP rules for ${moduleKey}`, e);
        throw e;
      }
    },

    async getOrganizationSettings(refresh = false) {
      try {
        const { data } = await axios.get('organization/', recentlyFailedOrgSettings() ? { hideToast: true } : {})
        await this.getGranularPermissions()

        this.organizationSettings = data
        this.organization = data
        sessionStorage.removeItem(ORG_SETTINGS_FAILURE_KEY)

        return this.organizationSettings as Organization
      } catch (e) {
        sessionStorage.setItem(ORG_SETTINGS_FAILURE_KEY, String(Date.now()))
        throw e
      }
    },

    async getOrganizationIntegrationSettings(refresh = false) {
      if (!hasUserPermissions(['@pollux.view.main.organization.integration'])) return []
      const { data } = await axios.get('organization-settings/')
      return data as OrganizationSetting[]
    },

    // GET <LIST> requests
    async getWyebotNetworkTestProfiles({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: add permissions
      const { data } = await axios.get(`wyebot/network-test-profiles/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as CachedPaginated<WyebotNetworkTestProfile>
    },
    async getWyebotSites({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: add permissions
      const { data } = await getOrFile(`wyebot/sites/`, {
        page,
        pageSize,
        search,
        filters,
      })
      return data as CachedPaginated<WyebotSite>
    },
    async getWyebotSensors({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: add permissions
      const { data } = await getOrFile(`wyebot/sensors/`, {
        page,
        pageSize,
        search,
        filters,
      })
      return data as CachedPaginated<WyebotSensor>
    },
    async getVerkadaDoors({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: add permissions
      const { data } = await axios.get(`verkada/access/doors/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as CachedPaginated<VerkadaDoor>
    },
    async getVerkadaSites({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: add permissions
      const { data } = await getOrFile(`verkada/sites/`, {
        page,
        pageSize,
        search,
        filters,
      })
      return data as CachedPaginated<VerkadaSite>
    },
    async getVerkadaAccessControllers({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: add permissions
      const { data } = await axios.get(`verkada/access/controller-units/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as CachedPaginated<VerkadaACU>
    },
    async getVerkadaCameras({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: add permissions
      const { data } = await getOrFile(`verkada/cameras/`, {
        page,
        pageSize,
        search,
        filters,
      })
      return data as CachedPaginated<VerkadaCamera>
    },
    async getVPNPeers({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: add permissions
      const { data } = await axios.get(`vpn-peers/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as Paginated<VPNPeerName>
    },
    async getVLANS({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: add permissions
      const { data } = await getOrFile(`subnet-info/`, {
        page,
        pageSize,
        search,
        filters: {
          ...filters,
          ordering: "-created_at",
        }
      })

      return data as VLAN[]
    },
    async getSystemLogs({ page = 1, pageSize = 10, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.main.user'])) return defaultPagination()
      const { data } = await getOrFile('logs/', {
        page,
        pageSize,
        search,
        filters,
      })
      return data as Paginated<SystemLog>
    },

    /**
     * Parses Fixed IP API error responses and extracts meaningful error messages
     */
    parseFixedIPError(error: any, defaultMessage: string): string {
      if (!error.response || !error.response.data) {
        return defaultMessage;
      }

      const status = error.response.status;
      const responseData = error.response.data;

      if (status === 500 && responseData.detail) {
        // For 500 errors, extract error from the details string
        const match = responseData.detail.match(/'errors':\s*\['([^']+)'\]/);
        if (match && match[1]) {
          return match[1];
        }
        // If no match found, return the full details
        return responseData.detail;
      } else if (status === 400) {
        // For 400 errors, get all error messages from response keys
        if (typeof responseData === 'object') {
          const allErrors = Object.values(responseData).flat();
          if (allErrors.length > 0) {
            return allErrors.join(', ');
          }
        }
      }

      return defaultMessage;
    },

    async deleteFixedIPByMAC(vlanId: string, mac: string, toast: any) {
      // if (!hasUserPermissions(['@meraki.delete.fixed_ip'])) {
      //   toast(toastError("You do not have permission to perform this action!"));
      //   return false;
      // }

      try {
        const params = await signWithElevatedPermissions(toast)
        await axios.delete(`vlan/${vlanId}/`, {
          params,
          data: { mac },
        });

        toast(toastSuccess("Fixed IP assignment successfully deleted"), TOAST_SUCCESS_OPTIONS);
        return true;
      } catch (e: any) {
        return false;
      }
    },
    async createFixedIP(vlanId: string, fixedIpData: { mac: string; ip: string; name?: string }, toast: any) {
      // if (!hasUserPermissions(['@meraki.create.fixed_ip'])) {
      //   toast(toastError("You do not have permission to perform this action!"));
      //   return false;
      // }

      const signedData = await signDictWithElevatedPermissions(fixedIpData, toast);
      const { data } = await axios.post(`vlan/${vlanId}/`, signedData);

      toast(toastSuccess("Fixed IP assignment successfully created"), TOAST_SUCCESS_OPTIONS);
      return data;
    },
    async updateFixedIP(vlanId: string, fixedIpData: { mac: string; ip: string; name?: string }, toast: any) {
      // if (!hasUserPermissions(['@meraki.update.fixed_ip'])) {
      //   toast(toastError("You do not have permission to perform this action!"));
      //   return false;
      // }

      const signedData = await signDictWithElevatedPermissions(fixedIpData, toast);
      const { data } = await axios.post(`vlan/${vlanId}/`, signedData);

      toast(toastSuccess("Fixed IP assignment successfully updated"), TOAST_SUCCESS_OPTIONS);
      return data;
    },
    async getNetworkNamingStrategy() {
      const { data } = await axios.get(`strategy/network-naming/`)
      return data as NetworkNamingConvention
    },
    async getLocationNamingStrategy() {
      const { data } = await axios.get(`strategy/location-naming/`)
      return data as LocationNamingConvention
    },
    async getDeveloperAPIKeys() {
      if (!hasUserPermissions(['@pollux.view.main.devapikey'])) return []

      const { data } = await axios.get(`api-keys/`)
      return data as APIKey[]
    },
    async getOrganizationModulePermissions() {
      const { data } = await axios.get(`module-permissions/`)
      this.organizationModulePermissions = data
      return data as OrganizationModulePermission[]
    },
    async getCountries(bypassCache = false) {
      if (!bypassCache && this.countries && this.countries.length > 0) return this.countries;
      const { data } = await axios.get(`countries/`)
      this.countries = data as Country[]
      return data as Country[]
    },
    async getIPAM({ page = 1, pageSize = 1, search = "", filters = {} }) {
      const { data } = await getOrFile("ipam/", {
        page,
        pageSize,
        search,
        filters,
      })
      return data as Paginated<IPAM>
    },
    async getLocationTypes(withCount = false, gp=false, bypassCache = false) {
      if (!hasOrganizationModulePermissions(['LOCATION_TYPE'])) return []
      if (!hasUserPermissions(['@pollux.view.location.type'])) return []

      const cacheKey = `withCount=${withCount}&gp=${gp}`;
      this._locationTypesCache = this._locationTypesCache || {};

      if (!bypassCache && this._locationTypesCache[cacheKey]) {
        this.locationTypes = this._locationTypesCache[cacheKey];
        return this.locationTypes;
      }

      const { data } = await axios.get(`location-types/?location_count=${withCount ? 1 : 0}&gp=${gp ? 1 : 0}`)
      this.locationTypes = data as LocationType[]
      this._locationTypesCache[cacheKey] = this.locationTypes;
      return this.locationTypes
    },
    async getLocationTypesWithCount() {
      return await this.getLocationTypes(true)
    },
    async getLocationTypeImages() {
      if (!hasUserPermissions(['@pollux.view.location.type'])) return []
      if (!hasOrganizationModulePermissions(['LOCATION_TYPE'])) return []
      const { data } = await axios.get(`location-type-images/`, {
        params: {
          ordering: "name"
        }
      })
      return data as Icon[]
    },
    async getDeviceTypes(withCount = false) {
      if (!hasUserPermissions(['@pollux.view.meraki.device.all'])) return []
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return []
      const { data } = await axios.get(`device-types/?device_count=${withCount ? 1 : 0}`)
      return data as DeviceType[]
    },
    async getDeviceTypesWithCount() {
      if (!hasUserPermissions(['@pollux.view.meraki.device.all'])) return []
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return []
      return (await this.getDeviceTypes(true)) as DeviceType[]
    },

    async getLocationBranches(withCount = false, gp = false, bypassCache = false) {
      if (!hasOrganizationModulePermissions(['LOCATION_BRANCH'])) return []
      if (!hasUserPermissions(['@pollux.view.location.branch'])) return []
      
      const cacheKey = `withCount=${withCount}&gp=${gp}`;
      this._locationBranchesCache = this._locationBranchesCache || {};

      if (!bypassCache && this._locationBranchesCache[cacheKey]) {
        this.locationBranches = this._locationBranchesCache[cacheKey];
        return this.locationBranches;
      }

      const { data } = await axios.get(`location-branches/?location_count=${withCount ? 1 : 0}&gp=${gp ? 1 : 0}`)
      this.locationBranches = data as Branch[]
      this._locationBranchesCache[cacheKey] = this.locationBranches;
      return this.locationBranches
    },
    async getLocationBranchesWithCount() {
      return await this.getLocationBranches(true)
    },

    async getLocationStatus() {
      const { data } = await axios.get("location-status/")
      return data as LocationStatus[]
    },

    async getLocations({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.location.overview'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['LOCATION'])) return defaultPagination()

      const { data } = await getOrFile(`locations/`, {
        page,
        pageSize,
        search,
        filters: {
          ...filters,
          ordering: "network,-created_at",
        },
      })

      return data as Paginated<Location>
    },

    async getLocationsForCameraAccess({ page = 1, pageSize = 10, search = "" }) {
      const { data } = await axios.get(`cameras/locations/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
        }
      })
      return data as Paginated<Location>
    },

    async getLocationsByType({ page = 1, pageSize = 1, search = "", filters = {} }, locationType: LocationType) {
      if (!hasUserPermissions(['@pollux.view.location.overview', '@pollux.view.location.type'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['LOCATION', 'LOCATION_TYPE'])) return defaultPagination()

      const { data } = await getOrFile(`locations/`, {
        page,
        pageSize,
        search,
        filters: {
          ...filters,
          location_type: locationType.code,
          ordering: "network,-created_at",
        },
      })

      return data as Paginated<Location>
    },
    async getLocationWithoutNetworkByType({ page = 1, pageSize = 1, search = "", filters = {} }, locationType: LocationType) {
      if (!hasUserPermissions(['@pollux.view.location.overview', '@pollux.view.location.type', '@pollux.view.meraki.network'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['LOCATION', 'LOCATION_TYPE', 'MERAKI_MAIN'])) return defaultPagination()
      const { data } = await axios.get(`locations/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          location_type: locationType.code,
          assigned_network: false,
          ...filters,
        }
      })
      return data as Paginated<Location>
    },
    async getLocationsWithoutNetwork({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.location.overview', '@pollux.view.meraki.network'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['LOCATION', 'MERAKI_MAIN'])) return defaultPagination()
      const { data } = await axios.get(`locations/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          assigned_network: false,
          ...filters,
        }
      })
      return data as Paginated<Location>
    },
    async getLocationsWithoutVerkadaSite({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: Implement permissions
      const { data } = await axios.get(`locations/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          assigned_verkada_site: false,
          ...filters,
        }
      })
      return data as Paginated<Location>
    },
    async getLocationsWithoutWyebotSite({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: Implement permissions
      const { data } = await axios.get(`locations/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          assigned_wyebot_site: false,
          ...filters,
        }
      })
      return data as Paginated<Location>
    },
    async getLocationsWithoutDocumentation({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.location.overview'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['LOCATION', 'LOCATION_DOCUMENTATION'])) return defaultPagination()
      const { data } = await axios.get(`locations/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          documentation: 0,
          ...filters
        }
      })
      return data as Paginated<Location>
    },
    async getLocationsWithDocumentation({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.location.documentation'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['LOCATION', 'LOCATION_DOCUMENTATION'])) return defaultPagination()
      const { data } = await axios.get(`location-documentations/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          documentation: 1,
          ...filters,
        }
      })
      return data as Paginated<Location>
    },
    async getProcedureDocumentations({ page=1, pageSize=1, search="", filters={}}){
      if(!hasUserPermissions(['@pollux.view.documentation.procedures'])) return defaultPagination()
      if(!hasOrganizationModulePermissions(['PROCEDURE_DOCUMENTATION'])) return defaultPagination()

      const {data} = await axios.get(`procedure-documentations/`, { 
        params: { 
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        } 
      })
      return data as Paginated<Procedure>
    },
    async getLocationsWorldmap(){
      if(!hasUserPermissions(['@pollux.view.location.overview'])) return []
      if(!hasOrganizationModulePermissions(['LOCATION'])) return []
      const {data} = await axios.get(`locations/`, { 
        params: { 
          all: true,
          mapview: true,
        }
      })
      this.worldMapLocations = data
      return data as Location[]
    },

    async getDevices({ page = 1, pageSize = 1, search = "", filters = {}, ordering = "id" }) {
      if (!hasAnyUserPermissions(bundleShortcuts.merakiDeviceAny)) return defaultPagination()
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return defaultPagination()

      const { data } = await getOrFile(`devices/`, {
        page,
        pageSize,
        search,
        filters: {
          ...filters,
          ordering,
        },
      })

      return data as Paginated<Device>
    },

    async getDevicesByType({ page = 1, pageSize = 1, search = "", filters = {} }, type: string) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return defaultPagination()

      const { data } = await getOrFile(`devices/`, {
        page,
        pageSize,
        search,
        filters: {
          ...filters,
          type__meraki_name: type,
          ordering: "id",
        },
      })

      return data as Paginated<Device>
    },
    async getDevicesByNetworkID({ page = 1, pageSize = 1, search = "", filters = {} }, networkID: ID) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return defaultPagination()

      const { data } = await getOrFile(`devices/`, {
        page,
        pageSize,
        search,
        filters: {
          ...filters,
          network: networkID,
          ordering: "name",
        },
      })

      return data as Paginated<Device>
    },
    /**
     * A device can only be claimed into the Meraki organization it already lives in, so the
     * caller must say which region it is picking for. Defaults to Global.
     */
    async getUnassignedDevicesByType({ page = 1, pageSize = 1, search = "", filters = {} }, type: string, isChina = false) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return defaultPagination()
      const { data } = await axios.get(`devices/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          type__meraki_name: type,
          claimed: false,
          is_china: isChina,
          ordering: "name",
          exclude_vmx: true,
          ...filters,
        }
      })
      return data as Paginated<Device>
    },
    async getUnassignedDevicesByTypes({ page = 1, pageSize = 1, search = "", filters = {} }, types: string[], isChina = false) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return defaultPagination()
      const { data } = await axios.get(`devices/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          type__meraki_name__in: JSON.stringify(types),
          claimed: false,
          is_china: isChina,
          ordering: "id",
          ...filters,
        }
      })
      return data as Paginated<Device>
    },


    async getNetworks({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.meraki.network'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['LOCATION:LIST,UPDATE', 'MERAKI_MAIN:LIST,UPDATE'])) return defaultPagination()

      const { data } = await getOrFile(`networks/`, {
        page,
        pageSize,
        search,
        filters,
      })

      return data as Paginated<Network>
    },

    async getVPNStatus({ page = 1, pageSize = 25, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.meraki.vpn_status'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN', 'VPN_STATUS'])) return defaultPagination()

      const { data } = await getOrFile(`vpn-status/`, {
        page,
        pageSize,
        search,
        filters,
      })

      return data as Paginated<VPNStatus>
    },
    async getVPNStatusWorldmap() {
      if (!hasUserPermissions(['@pollux.view.meraki.vpn_status'])) return []
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN', 'VPN_STATUS'])) return []
      const { data } = await axios.get(`vpn-status/`, {
        params: {
          all: true,
          mapview: true,
        }
      })
      return data as VPNStatus[]
    },

    async getVPNStatusByNetworkID(networkID: ID) {
      if (!hasUserPermissions(['@pollux.view.meraki.vpn_status', '@pollux.view.meraki.network'])) return null
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN', 'VPN_STATUS'])) return null
      const { data } = await axios.get(`vpn-status/?network_id=${networkID}`)
      return data.results[0] as VPNStatus
    },

    async geocodeAddress(query: any) {
      if (query.length < 1) return;
      const { data } = await axios.get(`locations/geocode-address/?q=${query}`)
      return data as GeocodeSearchResponse;
    },

    async getAllTopdeskTickets({ operator = "", location_internal_id = "", email_suffix = "" }: TopdeskFilters = { operator: "", location_internal_id: "", email_suffix: "" }) {
      if (!hasUserPermissions(['@pollux.view.support_desk.tickets'])) return []
      if (!hasOrganizationModulePermissions(['SUPPORT_DESK', 'SUPPORT_DESK_TICKETS'])) return []
      const { data } = await axios.get(`topdesk-tickets/?operator=${operator}&location_internal_id=${location_internal_id}&email_suffix=${encodeURIComponent(email_suffix)}`)
      if (email_suffix != "") {
        // Configurable requester-suffix tab (e.g. Pollux).
        this.tickets.custom = data
      } else if (operator == "") {
        this.openTicketsCount = data.filter(x => x.status != "Completed").length
        this.completedTicketsCount = data.filter(x => x.status == "Completed").length
        this.tickets.all = data
      } else {
        Object.keys(servicedeskIssuers).forEach((key) => {
          if (operator == key) this.tickets[servicedeskIssuers[key]] = data
        })
      }

      return data as TopdeskTicket[]
    },
    async getSupportDeskSettings() {
      const { data } = await axios.get(`support-desk-settings/`)
      this.supportDeskTicketTab = data
      return data as SupportDeskTicketTab
    },
    async modifySupportDeskSettings(supportDeskTicketTabRequest: SupportDeskTicketTab, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.support_desk.settings'])) {
        toast(toastError("You do not have permission to change support desk settings!"));
        return
      }
      try {
        const { data } = await axios.patch(`support-desk-settings/`, supportDeskTicketTabRequest)
        this.supportDeskTicketTab = data
        toast(toastSuccess("Support desk settings were successfully changed!"), TOAST_SUCCESS_OPTIONS);
        return data as SupportDeskTicketTab
      } catch (e: any) {
        toast(toastError("Failed to change your support desk settings!"));
        throw e
      }
    },
    async getAllTopdeskChanges({ operator = "", location_internal_id = "" }: TopdeskFilters = { operator: "", location_internal_id: "" }) {
      if (!hasUserPermissions(['@pollux.view.support_desk.changes'])) return []
      if (!hasOrganizationModulePermissions(['SUPPORT_DESK', 'SUPPORT_DESK_TICKETS'])) return []
      const { data } = await axios.get(`topdesk-changes/?operator=${operator}&location_internal_id=${location_internal_id}`)
      if (location_internal_id == "") {
        this.changes = data
      }
      return data as TopdeskChange[]
    },

    async getServiceReports() {
      if (!hasUserPermissions(['@pollux.view.support_desk.service_reports'])) return []
      if (!hasOrganizationModulePermissions(['SUPPORT_DESK', 'SERVICE_REPORTS'])) return []
      const { data } = await axios.get("service-reports/")
      return data as UploadedFile[]
    },

    async getNetworkSizeChoices() {
      if (!hasOrganizationModulePermissions(['BLUEPRINTS'])) return []
      const { data } = await axios.get("network-sizes/")
      return data
    },

    async getNetworkRanges({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.ipam_blueprint.network_range'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['BLUEPRINTS', 'MERAKI_MAIN'])) return defaultPagination()
      const { data } = await axios.get("network-ranges/", {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as Paginated<NetworkRange>
    },

    async getVlanRules({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.ipam_blueprint.vlan_rule'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['BLUEPRINTS'])) return defaultPagination()
      const { data } = await axios.get("vlan-rules/", {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as Paginated<VLANRule>
    },

    async getVlanRulesByNetworkRangeID({ page = 1, pageSize = 1, search = "", filters = {} }, networkRangeID: ID) {
      if (!hasUserPermissions(['@pollux.view.ipam_blueprint.vlan_rule'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['BLUEPRINTS'])) return defaultPagination()
      const { data } = await axios.get(`vlan-rules/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          network_range: networkRangeID,
          ...filters,
        }
      })
      return data as Paginated<VLANRule>
    },

    async getVlanRulesByCountryID({ page = 1, pageSize = 1, search = "", filters = {} }, country_id: ID) {
      if (!hasUserPermissions(['@pollux.view.ipam_blueprint.vlan_rule'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['BLUEPRINTS'])) return defaultPagination()
      const { data } = await axios.get(`vlan-rules/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          country_rules: country_id,
          ...filters,
        }
      })
      return data as Paginated<VLANRule>
    },

    async getVlanRuleActions({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.ipam_blueprint.vlan_rule_action'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['BLUEPRINTS'])) return defaultPagination()
      const { data } = await axios.get("vlan-rule-actions/", {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as Paginated<VLANRuleAction>
    },

    async getVlanRuleActionsByVlanRuleID({ page = 1, pageSize = 1, search = "", filters = {} }, vlan_rule_id: ID) {
      if (!hasUserPermissions(['@pollux.view.ipam_blueprint.vlan_rule_action'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['BLUEPRINTS'])) return defaultPagination()
      const { data } = await axios.get(`vlan-rule-actions/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          vlan_rules: vlan_rule_id,
          ...filters,
        }
      })
      return data as Paginated<VLANRuleAction>
    },

    async getMerakiTemplateRules({ page = 1, pageSize = 1, search = "", filters = {} }) {
      // TODO: Add permissions
      const { data } = await axios.get(`network-config-template-rules/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as Paginated<TemplateRule>
    },

    async getNetworkSizes() {
      if (!hasOrganizationModulePermissions(['BLUEPRINTS'])) return []
      const { data } = await axios.get("network-sizes/")
      return data as NetworkSize[]
    },

    async getOrganizationSecrets() {
      if (!hasUserPermissions(['@pollux.view.main.organization.integration'])) return []
      const { data } = await axios.get("organization-settings/")
      return data as OrganizationSecret[]
    },

    async getIntegrations() {
      if (!hasUserPermissions(['@pollux.view.main.organization.integration'])) return []
      const { data } = await axios.get("organization-setting-types/")
      return data as Integration[]
    },

    async getUsers({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.main.user'])) return defaultPagination()
      const { data } = await axios.get("users/", {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as Paginated<User>
    },
    async getInvitations({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.main.invitation'])) return defaultPagination()
      const { data } = await axios.get("user-invitations/", {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as Paginated<Invitation>
    },

    async getMagicLinks({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.security.magic_link'])) return defaultPagination()
      const { data } = await axios.get("magic-links/", {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as Paginated<MagicLink>
    },

    async getConnectedClients({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.meraki.client'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN', 'MERAKI_CLIENTS'])) return defaultPagination()

      const { data } = await getOrFile(`connected-clients/`, {
        page,
        pageSize,
        search,
        filters,
      })

      return data as Paginated<ConnectedClient>
    },
    async getConnectedClientsByLocation({ page = 1, pageSize = 1, search = "", filters = {} }, locationID) {
      if (!hasUserPermissions(['@pollux.view.meraki.client'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN', 'MERAKI_CLIENTS', 'LOCATION'])) return defaultPagination()

      const { data } = await getOrFile(`connected-clients/`, {
        page,
        pageSize,
        search,
        filters: {
          ...filters,
          location_id: locationID
        },
      })

      return data as Paginated<ConnectedClient>
    },
    async getConnectedClientsByNetwork({ page = 1, pageSize = 1, search = "", filters = {} }, networkID) {
      if (!hasUserPermissions(['@pollux.view.meraki.client', '@pollux.view.meraki.network'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN', 'MERAKI_CLIENTS'])) return defaultPagination()

      const { data } = await getOrFile(`connected-clients/`, {
        page,
        pageSize,
        search,
        filters: {
          ...filters,
          network_id: networkID,
        },
      })

      return data as Paginated<ConnectedClient>
    },

    async getSpeedtests({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.location.speedtest'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['LOCATION', 'SPEEDTEST'])) return defaultPagination()
      const { data } = await axios.get("speedtest-data/", {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as Paginated<speedtest>
    },

    async getSpeedtestByNetworkID(networkID) {
      if (!hasUserPermissions(['@pollux.view.location.speedtest', '@pollux.view.meraki.network'])) return null
      if (!hasOrganizationModulePermissions(['LOCATION', 'SPEEDTEST', 'MERAKI_MAIN'])) return null
      const { data } = await axios.get("speedtest-data/", {
        params: {
          p: 1,
          page_size: 1,
          network: networkID,
        }
      })
      if (data.results.length)
        return data.results[0] as speedtest
      else return null
    },

    async getRoles() {
      if (!hasUserPermissions(['@pollux.view.main.role'])) return []
      const { data } = await axios.get("roles/")
      return data as Role[]
    },


    async getVersions() {
      const { data } = await axios.get("static/version/index.json")
      return data as Versions
    },
    async getCurrentVersionNumber() {
      return (await this.getVersions()).current as string
    },
    async getVersion2() {
      const { data } = await axios.get("pstatic/version/2.x.x.x.json")
      return data as Version[]
    },
    async getVersion(version) {
      // For security reasons parse the version, this way we do not just fetch any frontend URL
      const versionSplit = version.split(".")
      const base = parseInt(versionSplit[0])
      const main = parseInt(versionSplit[1])
      const sub = parseInt(versionSplit[2])
      const patch = parseInt(versionSplit[3])

      let versionParsed = `${base}.${main}`
      if (!isNaN(sub)) versionParsed += `.${sub}`
      if (!isNaN(patch) && !isNaN(sub)) versionParsed += `.${patch}`
      else if (patch) versionParsed += `.0.${patch}`

      let data = null
      if (base == 3) {
        ({ data } = await axios.get(`pstatic/version/${versionParsed}.json`))
      } else if (base == 2) {
        ({ data } = await axios.get(`pstatic/version/2.x.x.x.json`))
      }

      // version 2 does not have seperate files, so we need to filter the specific version
      // out of the list of versions
      if (base == 2) data = data.find(v => v.version == versionParsed)
      return data as Version
    },
    async getCurrentVersion() {
      return (await this.getVersion(await this.getCurrentVersionNumber())) as Version
    },
    async getLegacyStatus() {
      const { data } = await axios.get("about/legacy/")
      return data.is_legacy
    },

    async getAllCountries() {
      const { data } = await axios.get("timezone-countries/")
      return data as GeocodeCountry[]
    },
    async getAllTimezones() {
      const { data } = await axios.get("timezones-detailed/")
      return data as GeoCodeTimezone[]
    },
    async getAllTimezonesByCountryCode(countryCode: string) {
      const { data } = await axios.get("timezones-detailed/", {
        params: {
          country_code: countryCode,
        }
      })
      return data as GeoCodeTimezone[]
    },

    async getTroubledInternetConnectionsByIssueType({ page = 1, pageSize = 1, search = "", filters = {} }, issueType: string) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return defaultPagination()
      const { data } = await getOrFile("device-status/", {
        page,
        pageSize,
        search,
        filters: {
          ...filters,
          device_type: "MX",
          exclude_type: "VMX",
          issue: issueType,
        },
      })
      return data as Paginated<TroubledInternetConnection>
    },
    async getFailedInternetConnections({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return defaultPagination()
      const { data } = await getOrFile("uplink-status-interfaces/", {
        page,
        pageSize,
        search,
        filters: {
          ...filters,
          status: "failed",
        },
      })
      return data as Paginated<FailedInternetConnection>
    },
    async getRMSCellularBackups() {
      if (!hasOrganizationModulePermissions(['RMS_MAIN'])) return []
      const { data } = await axios.get("rms/cellular-backups/")
      return data as RMSCellularBackup[]
    },
    async getMerakiCellularBackups() {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN', 'MERAKI_CELLULAR_BACKUP', 'KPN_MAIN'])) return []
      const { data } = await axios.get("meraki/cellular-backups/")
      return data as MerakiCellularBackup[]
    },
    async getMerakiSubnets({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return []

      const { data } = await getOrFile(`meraki/subnet/`, {
        page,
        pageSize,
        search,
        filters,
      })

      return data as Subnet[]
    },
    async getSimCards({ page = 1, pageSize = 1, search = "", filters = {} }) {
      const { data } = await getOrFile(`sim-cards/`, {
        page,
        pageSize,
        search,
        filters,
      })
      return data as Paginated<SimCard>
    },
    async getSimCardByICCID(iccid: string) {
      const { data } = await axios.get(`sim-cards/${iccid}/`)
      return data as SimCard
    },
    async getSimCardStatuses() {
      const { data } = await axios.get(`sim-cards/statuses/`)
      return data as string[]
    },
    async getWyebotTests({ page = 1, pageSize = 1, search = "", filters = {} }) {
      const { data } = await axios.get(`wyebot/tests/`, {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as WyebotNetworkTest
    },


    // GET <DETAIL> requests
    async getSwitchportBySerialAndPortID(serial, portID) {
      if (!serial) throw new Error("Serial is required");
      if (!portID) throw new Error("PortID is required");

      const { data } = await axios.get(`/meraki/switchports/${serial}/${portID}/`);
      return data as SwitchPort;
    },
    async getWyebotCloudEnvironment() {
      // TODO: add permissions
      if (this.wyebotCloudEnvironment) return this.wyebotCloudEnvironment
      const { data } = await axios.get(`wyebot/cloud-environment/`)
      this.wyebotCloudEnvironment = data.cloud_environment
      return this.wyebotCloudEnvironment as string
    },
    async getVerkadaSiteByID(id: ID) {
      //TODO: Implement permissions
      const { data } = await axios.get(`verkada/sites/${id}/`)
      return data as VerkadaSite
    },
    async getWyebotSiteByID(id: ID) {
      //TODO: Implement permissions
      const { data } = await axios.get(`wyebot/sites/${id}/`)
      return data as WyebotSite
    },
    async getWyebotSensorByID(id: ID) {
      //TODO: Implement permissions
      const { data } = await axios.get(`wyebot/sensors/${id}/`)
      return data as WyebotSensor
    },
    async getMicrosoftSSORedirectURL() {
      const { data } = await axios.get(`login/microsoft`)
      return data.microsoft_sso_redirect_url as string
    },
    async getMerakiSettings() {
      const { data } = await axios.get(`meraki/settings/`)
      return data as MerakiSettings
    },
    async getLocationDocumentationByID(locationDocumentationID) {
      if (!hasUserPermissions(['@pollux.view.location.documentation'])) return null
      if (!hasOrganizationModulePermissions(['LOCATION_DOCUMENTATION'])) return null
      // callers probe this speculatively to check whether documentation exists;
      // a location without any is a normal 404, not a real error
      const { data } = await axios.get(`location-documentations/${locationDocumentationID}/`, { hideToast: true })
      this.detailLocationDocumentation = data
      return data as LocationDocumentation
    },
    async getProcedureDocumentationByID(procedureDocumentationID: ID){
      const {data} = await axios.get(`procedure-documentations/${procedureDocumentationID}/`)
      this.detailProcedureDocumentation = data
      return data as ProcedureDocumentation
    },
    async getLocationByID(id: ID){
      if (!hasOrganizationModulePermissions(['LOCATION'])) return null
      const { data } = await axios.get(`locations/${id}/`)
      this.detailLocation = data
      return data as Location
    },
    async getLocationTypeByID(id: ID) {
      if (!hasUserPermissions(['@pollux.view.location.type'])) return null
      if (!hasOrganizationModulePermissions(['LOCATION_TYPE'])) return null
      const { data } = await axios.get(`location-types/${id}/`)
      return data as LocationType
    },
    async getLocationBranchByID(id: ID) {
      if (!hasUserPermissions(['@pollux.view.location.branch'])) return null
      if (!hasOrganizationModulePermissions(['LOCATION_BRANCH'])) return null
      const { data } = await axios.get(`location-branches/${id}/`)
      return data as Branch
    },
    async getNetworkByID(id: ID) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return null
      const { data } = await axios.get(`networks/${id}/`)
      this.detailNetwork = data
      return data as Network
    },
    async getDeviceByID(id: ID) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return null
      const { data } = await axios.get(`devices/${id}/`)
      return data as Device
    },
    async getDeviceTypeByID(id: ID) {
      if (!hasUserPermissions(['@pollux.view.meraki.device.all'])) return null
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return null
      const { data } = await axios.get(`device-types/${id}/`)
      return data as DeviceType
    },
    async getDeviceStatusesByDeviceIDs(ids: ID[]) {
      if (!hasUserPermissions(['@pollux.view.meraki.device.all'])) return null
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return null
      const qs = ids.reduce((acc, val) => {
        return `device=${val}&` + acc
      }, '')
      const { data } = await axios.get(`device-status/?${qs}/`)
      return data
    },
    async getMessages({ page = 1, pageSize = 25, merge = false }: { page?: number, pageSize?: number, merge?: boolean } = {}) {
      const { data } = await axios.get(`messages/`, {
        params: { page, page_size: pageSize },
      })
      const results = data.results as Message[]
      this.messagesPageSize = pageSize
      if (page === 1 && merge) {
        // Polling refresh: dedupe-merge with already loaded messages so we don't
        // wipe out pages the user has already loaded via "Load more".
        const byId = new Map<ID, Message>()
        for (const m of this.messages as Message[]) byId.set(m.id, m)
        for (const m of results) byId.set(m.id, m)
        this.messages = [...byId.values()].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
      } else if (page === 1) {
        this.messages = results
      } else {
        this.messages = [...this.messages, ...results]
      }
      // `next` from page 1 always points at page 2, so it would be misleading
      // after the user has loaded further pages — use total count instead to
      // drive `hasMore` and the next page index.
      this.messagesNext = data.next
      this.messagesCount = data.count as number
      this.unreadMessagesCount = data.unread_count as number
      return data as Paginated<Message> & { unread_count: number }
    },
    async getMessageByID(id: ID) {
      const { data } = await axios.get(`messages/${id}/`)
      return data as Message
    },
    async getMessageSubMessages(id: ID, { page = 1, pageSize = 10 }: { page?: number, pageSize?: number } = {}) {
      const { data } = await axios.get(`messages/${id}/sub-messages/`, {
        params: { page, page_size: pageSize },
      })
      return data as Paginated<SubMessage>
    },
    async getTopdeskTicketByID(id: ID) {
      if (!hasUserPermissions(['@pollux.view.support_desk.tickets'])) return null
      if (!hasOrganizationModulePermissions(['SUPPORT_DESK_TICKETS'])) return null
      const { data } = await axios.get(`topdesk-tickets/${id}/`)
      return data as TopdeskTicketDetail
    },
    async getMicrosoftConfiguration() {
      if (env.VITE_NODE_ENV !== 'production') {
        this.microsoftSSOConfigured = false
        return false
      }
      try {
        await axios.get(`/login/microsoft/`, { hideToast: true })
        this.microsoftSSOConfigured = true
        return true
      } catch {
        this.microsoftSSOConfigured = false
        return false
      }
    },
    async getTopdeskChangeByID(id: ID) {
      if (!hasUserPermissions(['@pollux.view.support_desk.changes'])) return null
      if (!hasOrganizationModulePermissions(['SUPPORT_DESK_TICKETS'])) return null
      const { data } = await axios.get(`topdesk-changes/${id}/`)
      return data as TopdeskTicketDetail
    },
    async getOrganization() {
      try {
        const { data } = await axios.get(`organization/`)

        this.getMicrosoftConfiguration()

        localStorage.setItem('organizationSettings', JSON.stringify(data))
        this.organization = data
        this.organizationLoaded = true
        return data as Organization
      } catch (e) {
        this.organizationLoaded = true
      }
    },
    /**
     * Starts a Devices per Contract export in the background. Resolves to the export id,
     * or null after reporting why it could not start.
     */
    async startDevicesPerContractExport(toast: any): Promise<string | null> {
      try {
        // every failure is reported here, so the interceptor must not toast as well
        const { data } = await axios.post(`devices/export/devices-per-contract/`, null, { hideToast: true })
        return data.id
      } catch (e: any) {
        if (e?.response?.status === 403) {
          toast(toastError(apiErrorMessage(e, "Devices per Contract export is unavailable for this tenant.")))
          return null
        }

        toast(toastError("Failed to export devices per contract"))
        return null
      }
    },
    /**
     * Waits for a Devices per Contract export to finish and downloads it. The "export is
     * ready" notification links back to the devices page with the export id, so this also
     * serves exports that finished after the page was closed. Resolves to true once the
     * archive was saved, false after reporting why it was not.
     */
    async awaitDevicesPerContractExport(exportId: string, toast: any): Promise<boolean> {
      const statusUrl = `devices/export/devices-per-contract/${exportId}/`
      const maxAttempts = Math.ceil(DEVICES_PER_CONTRACT_POLL_TIMEOUT_MS / DEVICES_PER_CONTRACT_POLL_INTERVAL_MS)
      let finishedExport: DevicesPerContractExport | null = null

      for (let attempt = 0; attempt < maxAttempts && !finishedExport; attempt++) {
        if (attempt > 0) await sleep(DEVICES_PER_CONTRACT_POLL_INTERVAL_MS)

        let currentExport: DevicesPerContractExport
        try {
          currentExport = (await axios.get(statusUrl, { hideToast: true })).data
        } catch (e: any) {
          const httpStatus = e?.response?.status
          // Transient failures (network, 5xx) are retried; a 4xx means the record is gone.
          if (httpStatus && httpStatus < 500) {
            toast(toastError("Failed to export devices per contract"))
            return false
          }
          continue
        }

        if (currentExport.status === "done" || currentExport.status === "failed") {
          finishedExport = currentExport
        }
      }

      if (!finishedExport) {
        toast(toastError("The export is taking longer than expected. You will be notified when it is ready."))
        return false
      }

      if (finishedExport.status === "failed") {
        toast(toastError(finishedExport.error || "Failed to export devices per contract"))
        return false
      }

      try {
        const response = await axios.get(`${statusUrl}download/`, { responseType: "blob", hideToast: true })
        if (finishedExport.filename) {
          saveBlobAs(response.data, finishedExport.filename)
        } else {
          downloadFile(response.data, "zip", "devices-per-contract")
        }
      } catch (e: any) {
        toast(toastError("Failed to export devices per contract"))
        return false
      }

      // The backend posts the persistent "export is ready" notification, which the header
      // also toasts; a second success toast here would duplicate it.
      return true
    },
    async exportDevicesPerContract(toast: any) {
      if (!hasUserPermissions(['@pollux.export.meraki.devices_per_contract'])) return false

      const exportId = await this.startDevicesPerContractExport(toast)
      if (!exportId) return false

      toast(toastGeneral("Devices per Contract export started. The download starts automatically when it is ready."))

      return this.awaitDevicesPerContractExport(exportId, toast)
    },
    async getApplication() {
      const { data } = await axios.get(`about/`)
      return data as AboutApplication
    },
    async getNetworkRangeByID(id: ID) {
      if (!hasUserPermissions(['@pollux.view.ipam_blueprint.network_range'])) return null
      if (!hasOrganizationModulePermissions(['BLUEPRINTS', 'MERAKI_MAIN'])) return null
      const { data } = await axios.get(`network-ranges/${id}/`)
      return data as NetworkRange
    },
    async getVlanNetworkRangeByID(id: ID) {
      if (!hasUserPermissions(['@pollux.view.ipam_blueprint.network_range'])) return null
      if (!hasOrganizationModulePermissions(['BLUEPRINTS'])) return null
      const { data } = await axios.get(`network-ranges/${id}/`)
      return data as NetworkRange
    },
    async getVlanRuleByID(id: ID) {
      if (!hasUserPermissions(['@pollux.view.ipam_blueprint.vlan_rule'])) return null
      if (!hasOrganizationModulePermissions(['BLUEPRINTS'])) return null
      const { data } = await axios.get(`vlan-rules/${id}/`)
      return data as VLANRule
    },
    async getVlanRuleActionByID(id: ID) {
      if (!hasUserPermissions(['@pollux.view.ipam_blueprint.vlan_rule_action'])) return null
      if (!hasOrganizationModulePermissions(['BLUEPRINTS'])) return null
      const { data } = await axios.get(`vlan-rule-actions/${id}/`)
      return data as VLANRuleAction
    },
    async getMyTenants() {
      // Returns the tenants the current user belongs to (excluding the current
      // one and the public schema), with enough metadata to power the tenant
      // switcher in the profile dropdown. Result is cached on the store so the
      // dropdown can render synchronously after app load.
      const { data } = await axios.get('me/tenants/')
      this.myTenants = data
      return data as TenantSwitcherItem[]
    },
    async getUserByID(id: ID, store = true) {
      const { data } = await axios.get(`users/${id}/`)
      const perms = data.roles.map(x => x.permissions)

      this.detailUser = data

      if (store) {
        this.profile = data
        this.profile.is_loaded = true
        this.userData = data
        this.userPermissions = _.union(...perms)
      }

      return data as User
    },
    async getServiceReportByID(id: ID) {
      if (!hasUserPermissions(['@pollux.view.support_desk.service_reports'])) return null
      if (!hasOrganizationModulePermissions(['SERVICE_REPORTS'])) return null
      const { data } = await axios.get(`service-reports/${id}/`)
      return data as UploadedFile
    },
    async uploadServiceReport(reportData: { name: string, file: File }, toast: any) {
      if (!hasOrganizationModulePermissions(['SERVICE_REPORTS:CREATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }
      if (!hasUserPermissions(['@pollux.view.support_desk.service_reports'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      const formData = new FormData();
      formData.append('name', reportData.name);
      formData.append('file', reportData.file);

      const { data } = await axios.post(`service-reports/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        hideToast: true
      })

      toast(toastSuccess("Service report successfully uploaded!"), TOAST_SUCCESS_OPTIONS);
      return data as UploadedFile
    },
    async deleteServiceReportByID(id: ID, toast: any) {
      if (!hasOrganizationModulePermissions(['SERVICE_REPORTS:DELETE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return false
      }
      if (!hasUserPermissions(['@pollux.delete.support_desk.service_reports'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return false
      }
      try {
        await axios.delete(`service-reports/${id}/`)
        toast(toastSuccess("Service report successfully deleted!"), TOAST_SUCCESS_OPTIONS);
        return true
      } catch (e: any) {
        return false
      }
    },
    async getRoleByID(id: ID) {
      if (!hasUserPermissions(['@pollux.view.main.role'])) return null
      const { data } = await axios.get(`roles/${id}/`)
      this.detailRole = data
      return data as Role
    },
    async getInvitationByCode(code: string) {
      // Register.vue shows a dedicated invalid-code screen (or its own
      // toast for network errors) for this call, so the interceptor must
      // not also raise a toast for it
      const { data } = await axios.get(`user-invitations/${code}/`, { hideToast: true })
      return data as Invitation
    },
    async acceptInvitation(code: string, profile: Record<string, any>, config: AxiosRequestConfig = {}) {
      // If a real File/Blob is provided for profile_image we need multipart;
      // otherwise a plain JSON post is fine.
      const hasFile = profile.profile_image instanceof File || profile.profile_image instanceof Blob
      if (hasFile) {
        const fd = new FormData()
        for (const [k, v] of Object.entries(profile)) {
          if (v === undefined || v === null) continue
          fd.append(k, v as any)
        }
        await axios.post(`user-invitations/${code}/accept/`, fd, {
          ...config,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      } else {
        await axios.post(`user-invitations/${code}/accept/`, profile, config)
      }
    },
    async getVPNStatusLastSync() {
      if (!hasUserPermissions(['@pollux.view.meraki.vpn_status'])) return null
      if (!hasOrganizationModulePermissions(['VPN_STATUS'])) return null
      const { data } = await axios.get(`sync-info/VPN_STATUS/`)
      return data as LastSync
    },
    async getDeviceLastSync() {
      if (!hasUserPermissions(['@pollux.view.meraki.device.all'])) return null
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return null
      const { data } = await axios.get(`sync-info/DEVICE_INFO/`)
      return data as LastSync
    },
    async getClientsLastSync() {
      if (!hasUserPermissions(['@pollux.view.meraki.client'])) return null
      if (!hasOrganizationModulePermissions(['MERAKI_CLIENTS'])) return null
      const { data } = await axios.get(`sync-info/CONNECTED_CLIENTS/`)
      return data as LastSync
    },
    async getVLANLastSync() {
      if (!hasUserPermissions(['@pollux.view.meraki.network.vlan'])) return null
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return null
      const { data } = await axios.get(`sync-info/NETWORK_VLANS/`)
      return data as LastSync
    },
    async getNetworkLastSync() {
      if (!hasUserPermissions(['@pollux.view.meraki.network'])) return null
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN'])) return null
      const { data } = await axios.get(`sync-info/NETWORK/`)
      return data as LastSync
    },
    async getAllSyncInfo(config: AxiosRequestConfig = {}) {
      // Check for all required permissions
      const hasVPNPermission = hasUserPermissions(['@pollux.view.meraki.vpn_status']) && hasOrganizationModulePermissions(['VPN_STATUS'])
      const hasDevicePermission = hasUserPermissions(['@pollux.view.meraki.device.all']) && hasOrganizationModulePermissions(['MERAKI_MAIN'])
      const hasClientPermission = hasUserPermissions(['@pollux.view.meraki.client']) && hasOrganizationModulePermissions(['MERAKI_CLIENTS'])
      const hasNetworkPermission = hasUserPermissions(['@pollux.view.meraki.network']) && hasOrganizationModulePermissions(['MERAKI_MAIN'])
      const hasNetworkVlanPermission = hasUserPermissions(['@pollux.view.meraki.network.vlan']) && hasOrganizationModulePermissions(['MERAKI_MAIN'])
      const hasNetworkTemplatePermission = hasUserPermissions(['@pollux.view.meraki.network']) && hasOrganizationModulePermissions(['MERAKI_MAIN'])
      const hasWANStatusPermission = hasOrganizationModulePermissions(['MERAKI_MAIN'])
      const hasInventoryPermission = hasOrganizationModulePermissions(['MERAKI_MAIN'])
      const hasDeviceStatusPermission = hasUserPermissions(['@pollux.view.meraki.device.all']) && hasOrganizationModulePermissions(['MERAKI_MAIN'])
      const hasSimManagementPermission = hasUserPermissions(['@pollux.view.external_tools.sim_cards']) && hasOrganizationModulePermissions(['MODULE_EXT_TOOLS_SIM_CARDS'])
      const hasJamfManagementPermission = hasUserPermissions(['@pollux.view.external_tools.sim_cards']) && hasOrganizationModulePermissions(['JAMF_MAIN'])
      const hasMerakiIpamPermission = hasUserPermissions(['@pollux.view.ipam']) && hasOrganizationModulePermissions(['MERAKI_IPAM'])
      const hasVerkadaCameraPermission = hasUserPermissions(['@pollux.view.verkada.cameras']) && hasOrganizationModulePermissions(['VERKADA_MAIN', 'VERKADA_CAMERA'])
      const hasVerkadaAccessPermission = hasUserPermissions(['@pollux.view.verkada.access']) && hasOrganizationModulePermissions(['VERKADA_MAIN', 'VERKADA_ACCESS'])
      const hasVerkadaSitePermission = hasUserPermissions(['@pollux.view.verkada.sites']) && hasOrganizationModulePermissions(['VERKADA_MAIN'])
      const hasWyebotSitePermission = hasUserPermissions(['@pollux.view.wyebot.sites']) && hasOrganizationModulePermissions(['WYEBOT_MAIN'])
      const hasWyebotSensorPermission = hasUserPermissions(['@pollux.view.wyebot.sensors']) && hasOrganizationModulePermissions(['WYEBOT_MAIN', 'WYEBOT_SENSORS'])
      const hasWyebotTestsPermission = hasUserPermissions(['@pollux.view.wyebot.tests']) && hasOrganizationModulePermissions(['WYEBOT_MAIN', 'WYEBOT_TESTS'])
      const hasLocationCountrySyncPermission = hasUserPermissions(['@pollux.view.location.overview']) && hasOrganizationModulePermissions(['LOCATION'])

      const { data } = await axios.get('sync-info/', config)
      // Convert array to object with sync_type as keys
      const syncInfo = data.reduce((acc, item) => {
        acc[item.sync_type] = item
        return acc
      }, {})

      return {
        vpn: hasVPNPermission ? syncInfo['VPN_STATUS'] : null,
        device: hasDevicePermission ? syncInfo['DEVICE_INFO'] : null,
        device_status: hasDeviceStatusPermission ? syncInfo['DEVICE_STATUS'] : null,
        client: hasClientPermission ? syncInfo['CONNECTED_CLIENTS'] : null,
        network: hasNetworkPermission ? syncInfo['NETWORK'] : null,
        network_vlans: hasNetworkVlanPermission ? syncInfo['NETWORK_VLANS'] : null,
        network_template: hasNetworkTemplatePermission ? syncInfo['NETWORK_TEMPLATE'] : null,
        network_template_vlans: hasNetworkTemplatePermission ? syncInfo['NETWORK_TEMPLATE_VLANS'] : null,
        wan_status: hasWANStatusPermission ? syncInfo['WAN_STATUS'] : null,
        inventory: hasInventoryPermission ? syncInfo['INVENTORY'] : null,
        sim_management: hasSimManagementPermission ? syncInfo['SIM_MANAGEMENT'] : null,
        jamf_management: hasJamfManagementPermission ? syncInfo['JAMF_MANAGEMENT'] : null,
        meraki_ipam: hasMerakiIpamPermission ? syncInfo['MERAKI_IPAM'] : null,
        verkada_cameras: hasVerkadaCameraPermission ? syncInfo['VERKADA_CAMERAS'] : null,
        verkada_access_control: hasVerkadaAccessPermission ? syncInfo['VERKADA_ACCESS_CONTROLLERS'] : null,
        verkada_sites: hasVerkadaSitePermission ? syncInfo['VERKADA_SITES'] : null,
        verkada_access_doors: hasVerkadaAccessPermission ? syncInfo['VERKADA_ACCESS_DOORS'] : null,
        wyebot_sites: hasWyebotSitePermission ? syncInfo['WYEBOT_SITES'] : null,
        wyebot_sensors: hasWyebotSensorPermission ? syncInfo['WYEBOT_SENSORS'] : null,
        wyebot_test_results: hasWyebotTestsPermission ? syncInfo['WYEBOT_TEST_RESULTS'] : null,
        location_country_sync: hasLocationCountrySyncPermission ? syncInfo['LOCATION_COUNTRY_SYNC'] : null,
      }
    },
    async getMerakiSnapshotBySerail(serial: DeviceSerial, toast) {
      if (!hasOrganizationModulePermissions(['MERAKI_CAMERA_SNAPSHOT'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return null
      }


      const params = await signDictWithElevatedPermissions({
        serial,
      }, toast)

      try {
        const { data } = await axios.get("meraki/camera-snapshot/", {
          params
        })
        this.imageSnapshot = data.url
        return data as CameraSnapshot
      } catch (e: any) {
        toast(toastError(apiErrorMessage(e, "Failed to make a camera snapshot!")));
        throw e
      }
    },
    async getElevatedPermissions(toast) {
      await signWithElevatedPermissions(toast)
    },
    async getVerkadaSnapshotByID(cameraID: ID, toast) {
      if (!hasOrganizationModulePermissions(['VERKADA_CAMERA_SNAPSHOT'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return null
      }


      const params = await signWithElevatedPermissions(toast)

      const { data } = await axios.get(`verkada/cameras/${cameraID}/make-screenshot/`, {
        params,
        responseType: 'blob'
      })

      function blobToDataURL(blob) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result); // data URL string
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
      }

      const dataUrl = await blobToDataURL(data)
      this.imageSnapshot = dataUrl

      return dataUrl
    },
    async getSwitchPortStatusses(serial) {
      const { data } = await axios.get(`meraki/switchports/status/${serial}`)
      return data as SwitchportStatuses
    },
    async getSwitchportsBySerial({
      serial,
      page = 1,
      pageSize = 10,
      search = "",
      filters = {},
    }: {
      serial: string;
      page?: number;
      pageSize?: number;
      search?: string;
      filters?: Record<string, any>;
    }) {
      if (!serial) throw new Error("Serial is required");

      const params: Record<string, any> = { p: page, page_size: pageSize };

      if (search) params.search = search;
      if (filters) Object.assign(params, filters);

      const query = new URLSearchParams(params).toString();
      const url = `/meraki/switchports/${serial}/?${query}`;

      const { data } = await axios.get(url);
      return data as Paginated<SwitchPort>;
    },

    async updateSwitchPort({
      serial,
      portId,
      payload,
      toast,
    }: {
      serial: string;
      portId: string;
      payload: Partial<SwitchPort>;
      toast?: any;
    }) {
      if (!serial || !portId) throw new Error("Serial and portId are required");

      const url = `/meraki/switchports/${serial}/${portId}/update/`;

      payload = await signDictWithElevatedPermissions(payload, toast)

      try {
        const { data } = await axios.put(url, payload);

        if (toast) {
          toast(
            toastSuccess(`Port ${portId} on device ${serial} updated successfully!`),
            TOAST_SUCCESS_OPTIONS
          );
        }

        return data as SwitchPort;
      } catch (error: any) {
        // the toast is raised by the axios interceptor
        console.error("Error updating switch port:", error);
        throw error.response?.data || error;
      }
    },

    async cycleSwitchPort({
      serial,
      portId,
      toast,
    }: {
      serial: string;
      portId: string | number;
      toast?: any;
    }) {
      if (!serial || !portId) throw new Error("serial and portId are required");

      const url = `/meraki/switchports/${serial}/${portId}/cycle/`;

      try {
        const signedRequest = await signWithElevatedPermissions(toast)
        const { data } = await axios.post(url, signedRequest);
        if (toast) {
          toast(toastSuccess(`Switch port ${portId} on device ${serial} cycled succesfully!`), TOAST_SUCCESS_OPTIONS);
        }
        return data;
      } catch (error: any) {
        throw error.response?.data || error;
      }
    },

    // ---- MX appliance ports ----------------------------------------------------------------

    async getAppliancePortsBySerial({
      serial,
      page = 1,
      pageSize = 10,
      search = "",
      filters = {},
    }: {
      serial: string;
      page?: number;
      pageSize?: number;
      search?: string;
      filters?: Record<string, any>;
    }) {
      if (!serial) throw new Error("Serial is required");

      const params: Record<string, any> = { p: page, page_size: pageSize };
      if (search) params.search = search;
      if (filters) Object.assign(params, filters);

      const query = new URLSearchParams(params).toString();
      const { data } = await axios.get(`/meraki/applianceports/${serial}/?${query}`);
      return data;
    },

    async getApplianceUplinks(serial: string) {
      const { data } = await axios.get(`/meraki/applianceports/uplinks/${serial}/`);
      return data;
    },

    async getAppliancePortByNumber(serial: string, portNumber: string | number) {
      const { data } = await axios.get(`/meraki/applianceports/${serial}/${portNumber}/`);
      return data;
    },

    async startAppliancePortLiveStatus(serial: string) {
      // Returns null on Meraki-side failure (502 from our backend) so callers can degrade gracefully
      // to the static-only view rather than throwing.
      try {
        const { data } = await axios.post(`/meraki/applianceports/livestatus/${serial}/`);
        return data;
      } catch (error: any) {
        console.warn("[appliance] live-status start failed:", error?.response?.data ?? error);
        return null;
      }
    },

    async getAppliancePortLiveStatus(serial: string, jobId: string) {
      try {
        const { data } = await axios.get(`/meraki/applianceports/livestatus/${serial}/${jobId}/`);
        return data;
      } catch (error: any) {
        console.warn("[appliance] live-status poll failed:", error?.response?.data ?? error);
        return null;
      }
    },

    async cycleAppliancePort({
      serial,
      portNumber,
      toast,
    }: {
      serial: string;
      portNumber: string | number;
      toast?: any;
    }) {
      if (!serial || portNumber === undefined || portNumber === null) {
        throw new Error("serial and portNumber are required");
      }

      // Emulated cycle: the backend hands the disable -> wait -> re-enable off to a background
      // worker and returns 202 immediately. The final success/failure notification is delivered
      // asynchronously via the notifications (Messages) system, not this response. On up-front
      // rejection (409 template-bound / 400 already-disabled / 502) we surface the backend message.
      const url = `/meraki/applianceports/${serial}/${portNumber}/cycle/`;

      try {
        const signedRequest = await signWithElevatedPermissions(toast);
        const { data } = await axios.post(url, signedRequest);
        if (toast) {
          // 202 Accepted — the cycle is now running in the background.
          toast(
            toastSuccess(data?.message || `Cycle started for port ${portNumber} on ${serial}.`),
            TOAST_SUCCESS_OPTIONS,
          );
        }
        return data;
      } catch (error: any) {
        console.error("Error cycling appliance port:", error);
        if (toast) {
          try {
            toast(toastError(error.response.data.message));
          } catch (e) {
            toast(toastError(`Appliance port ${portNumber} on device ${serial} cycle failed!`));
          }
        }
        throw error.response?.data || error;
      }
    },

    async checkRefitExists(internalId: string, toast) {
      try {
        const response = await axios.get(`locations/${internalId}/refit-exists/`);
        return response.data.refit_exists;  // true or false
      } catch (e: any) {
        const errorMessage =
          e.response?.data?.detail || e.message || "Unknown error";
        throw e;
      }
    },
    async getSwitchportsTemplates({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.meraki.switchports'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['MERAKI_SWITCHPORTS'])) return defaultPagination()
      const { data } = await axios.get("meraki/switchports/templates/", {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as Paginated<SwitchportTemplates>
    },
    async getMXPortTemplates({ page = 1, pageSize = 1, search = "", filters = {} }) {
      if (!hasUserPermissions(['@pollux.view.meraki.mxports'])) return defaultPagination()
      if (!hasOrganizationModulePermissions(['MERAKI_MXPORTS'])) return defaultPagination()
      const { data } = await axios.get("meraki/mx/templates/", {
        params: {
          p: page,
          page_size: pageSize,
          search: search,
          ...filters,
        }
      })
      return data as Paginated<MXPortTemplates>
    },
    async refitPhase2Check(internalId: string, toast) {
      try {
        const response = await axios.get(`locations/${internalId}/refit-phase-2-check/`);
        return response.data.exists;  // true or false
      } catch (e: any) {
        const errorMessage =
          e.response?.data?.error || e.message || "Unknown error";
        throw e;
      }
    },

    // POST requests
    async refitPhase2(locationID: string, toast) {
      if(!hasUserPermissions(['@pollux.create.location.refit.phase_2'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      const signedRequest = await signWithElevatedPermissions(toast)

      const response = await axios.post(
        `locations/${locationID}/refit-phase-2/`,
        signedRequest
      );

      return response.data;
    },

    async duplicateLocation(locationID: string, toast) {
      const signedRequest = await signWithElevatedPermissions(toast)

      const response = await axios.post(
        `locations/${locationID}/duplicate/`,
        signedRequest
      );

      toast(
        toastSuccess("Refit location successfully created!"),
        TOAST_SUCCESS_OPTIONS
      );

      return response.data;
    },
    async createNewWyebotNetworkTest(newWyebotNetworkTestRequest: NewWyebotNetworkTestRequest, toast) {
      // TODO: add permissions
      newWyebotNetworkTestRequest = await signDictWithElevatedPermissions(newWyebotNetworkTestRequest, toast)

      const { data } = await axios.post('wyebot/tests/', newWyebotNetworkTestRequest, { hideToast: true })
      toast(toastSuccess("Wyebot network test was successfully created!"), TOAST_SUCCESS_OPTIONS);
      return data as APIKey
    },
    async unlockDoor(doorID, toast) {
      const bindDoorLocationRequest = await signWithElevatedPermissions(toast)

      // TODO: add permissions
      await axios.post(`verkada/access/doors/${doorID}/unlock/`, bindDoorLocationRequest)
      toast(toastSuccess("Verkada door was successfully unlocked!"), TOAST_SUCCESS_OPTIONS);
    },
    async unbindVerkadaSiteFromLocation(site_id, toast) {
      const bindSiteLocationRequest = await signWithElevatedPermissions(toast)

      // TODO: add permissions
      await axios.post(`verkada/sites/${site_id}/unbind/`, bindSiteLocationRequest)
      toast(toastSuccess("Verkada site was successfully disconnected!"), TOAST_SUCCESS_OPTIONS);
    },
    async unbindWyebotSiteFromLocation(site_id, toast) {
      const unbindSiteLocationRequest = await signWithElevatedPermissions(toast)

      // TODO: add permissions
      await axios.post(`wyebot/sites/${site_id}/unbind/`, unbindSiteLocationRequest)
      toast(toastSuccess("Wyebot site was successfully disconnected!"), TOAST_SUCCESS_OPTIONS);
    },
    async unbindWyebotSensorFromLocation(sensor_id, toast) {
      const unbindSensorLocationRequest = await signWithElevatedPermissions(toast)

      // TODO: add permissions
      await axios.post(`wyebot/sensors/${sensor_id}/unbind/`, unbindSensorLocationRequest)
      toast(toastSuccess("Wyebot sensor was successfully disconnected!"), TOAST_SUCCESS_OPTIONS);
    },
    async markSiteAsOrg(site_id, toast) {
      const markSiteRequest = await signWithElevatedPermissions(toast)

      // TODO: add permissions
      await axios.post(`wyebot/sites/${site_id}/mark-org/`, markSiteRequest)
      toast(toastSuccess("Wyebot site was successfully marked as an organisation!"), TOAST_SUCCESS_OPTIONS);
    },
    async unmarkSiteAsOrg(site_id, toast) {
      const unmarkSiteRequest = await signWithElevatedPermissions(toast)

      // TODO: add permissions
      await axios.post(`wyebot/sites/${site_id}/unmark-org/`, unmarkSiteRequest)
      toast(toastSuccess("Wyebot site was successfully unmarked as an organisation!"), TOAST_SUCCESS_OPTIONS);
    },
    async bindVerkadaSiteToLocation(site_id, location_id, toast, config: AxiosRequestConfig = {}) {
      const bindSiteLocationRequest = await signWithElevatedPermissions(toast)

      // TODO: add permissions
      await axios.post(`verkada/sites/${site_id}/bind/${location_id}/`, bindSiteLocationRequest, config)
      toast(toastSuccess("Verkada site and Pollux location were successfully connected!"), TOAST_SUCCESS_OPTIONS);
    },
    async bindWyebotSiteToLocation(site_id, location_id, toast, config: AxiosRequestConfig = {}) {
      const bindSiteLocationRequest = await signWithElevatedPermissions(toast)

      // TODO: add permissions
      await axios.post(`wyebot/sites/${site_id}/bind/${location_id}/`, bindSiteLocationRequest, config)
      toast(toastSuccess("Wyebot site and Pollux location were successfully connected!"), TOAST_SUCCESS_OPTIONS);
    },
    async bindWyebotSensorToLocation(sensor_id, location_id, toast, config: AxiosRequestConfig = {}) {
      const bindSensorLocationRequest = await signWithElevatedPermissions(toast)

      // TODO: add permissions
      await axios.post(`wyebot/sensors/${sensor_id}/bind/${location_id}/`, bindSensorLocationRequest, config)
      toast(toastSuccess("Wyebot sensor and Pollux location were successfully connected!"), TOAST_SUCCESS_OPTIONS);
    },
    async swapDevice(swapDeviceRequest: SwapDeviceRequest, toast) {
      swapDeviceRequest = await signDictWithElevatedPermissions(swapDeviceRequest, toast)

      // TODO: add permissions
      await axios.post('networks/swap-device/', swapDeviceRequest)
      toast(toastSuccess("Devices were succesfully swapped!"), TOAST_SUCCESS_OPTIONS);
    },
    async addNewDeviceToNetwork(addDeviceToNetworkRequest: AddDeviceToNetworkRequest, toast) {
      addDeviceToNetworkRequest = await signDictWithElevatedPermissions(addDeviceToNetworkRequest, toast)

      // TODO: add permissions
      try {
        await axios.post('networks/add-devices/', addDeviceToNetworkRequest)
        // toast(toastSuccess("Devices were succesfully added to network!"), TOAST_SUCCESS_OPTIONS); Moved to backend
      } catch (e: any) {
        // toast(toastError("Failed to add devices to network!")); Moved to backend
        throw e;
      }
    },
    async createNewAPIKey(newAPIKeyRequest: NewAPIKeyRequest, toast) {
      // TODO: add permissions
      if (!hasUserPermissions(['@pollux.manage.main.devapikey'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      newAPIKeyRequest = await signDictWithElevatedPermissions(newAPIKeyRequest, toast)

      const { data } = await axios.post('api-keys/', newAPIKeyRequest)
      toast(toastSuccess("API key was successfully created!"), TOAST_SUCCESS_OPTIONS);
      return data as APIKey
    },
    async replyToTopdeskTicket(ticketNumber: string, reply: string, toast: any) {
      await axios.post(`topdesk-tickets/${ticketNumber}/reply/`, {
        reply: reply
      })
      toast(
        toastSuccess("Reply to ticket was successfully added.")
      )
    },
    async replyToTopdeskChange(changeNumber: string, reply: string, toast: any){
      await axios.post(`topdesk-changes/${changeNumber}/reply/`, {
        reply: reply
      })
      toast(
        toastSuccess("Reply to change was successfully added.")
      )
    },
    async getTopdeskTicketAttachmentsList(ticketNumber: string){
      const { data } = await axios.get(`topdesk-tickets/${ticketNumber}/attachments/`)
      return data as TopdeskAttachment[]
    },
    async uploadTopdeskTicketAttachment(ticketNumber: string, file: File, toast: any, description?: string){
      const formData = new FormData()
      formData.append("file", file)
      if(description) formData.append("description", description)
      try{
        await axios.post(`topdesk-tickets/${ticketNumber}/attachments/`, formData)
        toast(toastSuccess("Attachment uploaded!"), TOAST_SUCCESS_OPTIONS);
      }catch(e: any){
        throw e
      }
    },
    async getTopdeskChangeAttachmentsList(changeNumber: string){
      const { data } = await axios.get(`topdesk-changes/${changeNumber}/attachments/`)
      return (Array.isArray(data) ? data : data?.results ?? []) as TopdeskAttachment[]
    },
    async uploadTopdeskChangeAttachment(changeNumber: string, file: File, toast: any){
      const formData = new FormData()
      formData.append("file", file)
      try{
        await axios.post(`topdesk-changes/${changeNumber}/attachments/`, formData)
        toast(toastSuccess("Attachment uploaded!"), TOAST_SUCCESS_OPTIONS);
      }catch(e: any){
        throw e
      }
    },
    /**
     * Downloads a file by fetching it through axios (so the request goes
     * through the configured proxy with authentication cookies) and triggering
     * a client-side blob download. Fetching directly via a bare <a> link would
     * bypass the proxy and have Vite serve index.html.
     */
    async downloadFileFromUrl(downloadUrl: string, fileName: string){
      const response = await axios.get(downloadUrl, { responseType: "blob" })
      const objectUrl = URL.createObjectURL(response.data)
      const a = document.createElement("a")

      try {
        a.href = objectUrl
        a.setAttribute("download", fileName || "download")
        document.body.appendChild(a)
        a.click()
      } finally {
        if (a.parentNode) {
          a.parentNode.removeChild(a)
        }
        URL.revokeObjectURL(objectUrl)
      }
    },
    async createBulkNewSubnets(filename: string, bulkCSVData: string, toast: any){
      // TODO: add permissions
      let params = null
      try {
        params = await signWithElevatedPermissions(toast)
      } catch (e) {
        throw {
          response: {
            data: {
              error: ["Elevated permissions must be granted to perform this action"]
            }
          }
        }
      }

      try {
        const { data } = await axios.post(`meraki/subnet/import/${filename}/`, bulkCSVData, {
          params,
          hideToast: true
        })
        toast(toastSuccess(data.message), TOAST_SUCCESS_OPTIONS);
      } catch (e: any) {
        // Error shown in StatusIndicatorText component, no toast needed
        throw e
      }
    },
    async transferLocationType(locationTypeTransferRequest: LocationTypeTransferRequest, toast: any) {
      if (!hasOrganizationModulePermissions(['LOCATION:UPDATE', 'LOCATION_TYPE:UPDATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      locationTypeTransferRequest = await signDictWithElevatedPermissions(locationTypeTransferRequest, toast)

      // a failure is announced by the axios interceptor
      const { data } = await axios.post('location-type-transfer/', locationTypeTransferRequest)
      toast(toastSuccess("Location type was successfully transferred!"), TOAST_SUCCESS_OPTIONS);
      return data as Role
    },
    async transferLocationBranch(locationBranchTransferRequest: LocationBranchTransferRequest, toast: any) {
      if (!hasOrganizationModulePermissions(['LOCATION:UPDATE', 'LOCATION_BRANCH:UPDATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      if (!hasOrganizationModulePermissions(['LOCATION_BRANCH'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      locationBranchTransferRequest = await signDictWithElevatedPermissions(locationBranchTransferRequest, toast)

      // a failure is announced by the axios interceptor
      const { data } = await axios.post('location-branch-transfer/', locationBranchTransferRequest)
      toast(toastSuccess("Location branch was successfully transferred!"), TOAST_SUCCESS_OPTIONS);
      return data as Role
    },
    async createNewRole(newRoleRequest: NewRoleRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.role'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      // a failure is announced by the axios interceptor
      const { data } = await axios.post('roles/', newRoleRequest)
      toast(toastSuccess("New role was successfully created!"), TOAST_SUCCESS_OPTIONS);
      const uiStore = useUIStore()
      uiStore.releaseUnsavedChanges()
      return data as Role
    },
    async createCameraAccessRequest(cameraAccessRequest: CameraAccessRequest, toast: any) {
      if (!hasOrganizationModulePermissions(['MERAKI_CAMERA_ACCESS:CREATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      cameraAccessRequest = await signDictWithElevatedPermissions(cameraAccessRequest, toast)

      try {
        const { data } = await axios.post('cameras/request-access/', cameraAccessRequest, { hideToast: true })
        toast(toastSuccess("Request to access camera footage was successfully created!"), TOAST_SUCCESS_OPTIONS);
        return data as Location
      } catch (e: any) {
        // Error shown in StatusIndicatorText component, no toast needed
        throw e;
      }
    },
    async createPasswordResetRequest(passwordResetRequest: PasswordResetRequest, toast: any) {
      const { data } = await axios.post('password-reset/', passwordResetRequest)
      toast(toastSuccess("Reset code was sent to your mailbox!"), TOAST_SUCCESS_OPTIONS);
      return data as Location
    },
    async verifyPasswordResetCode(code: string, passwordResetRequest: PasswordResetRequest, toast: any) {
      const { data } = await axios.post(`password-reset/${code}/`, passwordResetRequest, { hideToast: true })
      if (passwordResetRequest.new_password) {
        toast(toastSuccess("Successfully updated your password!"), TOAST_SUCCESS_OPTIONS);
      } else {
        toast(toastSuccess("Code is correct!"), TOAST_SUCCESS_OPTIONS);
      }
      return data as Location
    },
    async createNewLocation(newLocationRequest: NewLocationRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.location.location'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['LOCATION:CREATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const { data } = await axios.post('locations/', newLocationRequest, { hideToast: true })
      toast(toastSuccess("Location successfully created"), TOAST_SUCCESS_OPTIONS);
      return data as Location
    },
    async createBulkNewLocations(filename: string, bulkCSVData: string, toast: any) {
      if (!hasUserPermissions(['@pollux.import.location.location'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['LOCATION:CREATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      let params = null
      try {
        params = await signWithElevatedPermissions(toast)
      } catch (e) {
        throw {
          response: {
            data: {
              error: ["Elevated permissions must be granted to perform this action"]
            }
          }
        }
      }

      const { data } = await axios.post(`locations/upload-bulk/${filename}/`, bulkCSVData, {
        params,
        hideToast: true
      })
      toast(toastSuccess(data.message), TOAST_SUCCESS_OPTIONS);
    },
    async createNewUser(newUserRequest: NewUserRequest, toast: any) {
      const { data } = await axios.post(`users/`, newUserRequest, { hideToast: true })
      toast(toastSuccess('Successfully created an account!'), TOAST_SUCCESS_OPTIONS);
    },
    async createNewNetwork(newNetworkRequest: NewNetworkRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.meraki.network'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN:CREATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        throw new Error()
      }

      newNetworkRequest = await signDictWithElevatedPermissions(newNetworkRequest, toast)

      const { data } = await axios.post('networks/', newNetworkRequest, { hideToast: true })
      toast(toastGeneral("Network will be created in the background! You will receive a notification once it is done!"), TOAST_SUCCESS_OPTIONS);
      return data as Network
    },
    async createNewLocationBranch(newLocationBranchRequest: NewLocationBranchRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.location.branch'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['LOCATION_BRANCH:CREATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const { data } = await axios.post('location-branches/', newLocationBranchRequest, { hideToast: true })
      toast(toastSuccess("Location branch successfully created"), TOAST_SUCCESS_OPTIONS);
      return data as Branch
    },
    async createNewLocationType(newLocationTypeRequest: NewLocationTypeRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.location.type'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['LOCATION_TYPE:CREATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const { data } = await axios.post('location-types/', newLocationTypeRequest, { hideToast: true })
      toast(toastSuccess("Location type successfully created"), TOAST_SUCCESS_OPTIONS);
      return data as LocationType
    },
    async createNewNetworkRange(newNetworkRangeRequest: NetworkRangeRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.ipam_blueprint.network_range'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['BLUEPRINTS:CREATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const { data } = await axios.post(`network-ranges/`, newNetworkRangeRequest, { hideToast: true })
      toast(toastSuccess("Network Range successfully created!"), TOAST_SUCCESS_OPTIONS);
      return data as NetworkRange
    },
    async createNewVLANRule(newVLANRuleRequest: VLANRuleRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.ipam_blueprint.vlan_rule'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['BLUEPRINTS:CREATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const { data } = await axios.post(`vlan-rules/`, newVLANRuleRequest, { hideToast: true })
      toast(toastSuccess("VLAN rule successfully created!"), TOAST_SUCCESS_OPTIONS);
      return data as VLANRule
    },
    async createNewVLANRuleAction(newVLANRuleActionRequest: VLANRuleActionRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.ipam_blueprint.vlan_rule_action'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['BLUEPRINTS:CREATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const { data } = await axios.post(`vlan-rule-actions/`, newVLANRuleActionRequest, { hideToast: true })
      toast(toastSuccess("VLAN rule action successfully created!"), TOAST_SUCCESS_OPTIONS);
      return data as VLANRule
    },
    async rebootDeviceBySerial(serial: DeviceSerial, rebootDeviceRequest, toast: any) {
      if (!hasOrganizationModulePermissions(['MERAKI_DEVICE_REBOOT'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      // a failure is announced by the axios interceptor
      const { data } = await axios.post(`meraki/device-reboot/`, { ...rebootDeviceRequest, serial })
      toast(toastSuccess("Successfully rebooted device!"), TOAST_SUCCESS_OPTIONS);
      return data as Device
    },
    async createNewInvite(newInvitationRequest: NewInvitationRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.invitation'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      newInvitationRequest = await signDictWithElevatedPermissions(newInvitationRequest, toast)

      // a failure is announced by the axios interceptor
      const { data } = await axios.post(`user-invitations/`, newInvitationRequest)
      toast(toastSuccess("Invitation was successfully sent to user!"), TOAST_SUCCESS_OPTIONS);
      return data as Invitation
    },
    async createMagicLink(magicLinkRequest: any, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.security.magic_link'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      magicLinkRequest = await signDictWithElevatedPermissions(magicLinkRequest, toast)

      try {
        const { data } = await axios.post(`magic-links/`, magicLinkRequest)
        toast(toastSuccess("Magic Link was successfully created!"), TOAST_SUCCESS_OPTIONS);
        return data as MagicLink
      } catch (e: any) {
        throw e
      }
    },
    async expireMagicLink(id: string, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.security.magic_link'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      const rb = await signWithElevatedPermissions(toast)

      try {
        const { data } = await axios.post(`magic-links/${id}/expire/`, rb)
        toast(toastSuccess("Magic Link was successfully expired!"), TOAST_SUCCESS_OPTIONS);
        return data
      } catch (e: any) {
        throw e
      }
    },
    async renewMagicLink(id: string, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.security.magic_link'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      const rb = await signWithElevatedPermissions(toast)

      try {
        const { data } = await axios.post(`magic-links/${id}/renew/`, rb)
        toast(toastSuccess("Magic Link was successfully renewed!"), TOAST_SUCCESS_OPTIONS);
        return data
      } catch (e: any) {
        throw e
      }
    },
    async getMagicLinkToken(id: string, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.security.magic_link'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return null
      }
      try {
        const { data } = await axios.get(`magic-links/${id}/token/`)
        return data.token as string
      } catch (e: any) {
        throw e
      }
    },
    async toggleGuestWifi(network_id: ID, enable: boolean, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.meraki.guest_wifi'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      const rb = await signWithElevatedPermissions(toast)

      const { data } = await axios.post(`meraki/networks/${network_id}/guest-wi-fi/`, rb)
      toast(toastSuccess(`Guest wifi was successfully ${enable ? 'enabled' : 'disabled'}!`), TOAST_SUCCESS_OPTIONS);
      return data as Invitation
    },
    async clearAllNotifications(toast) {
      await axios.post(`messages/clear/`)
    },
    async markAllReadNotifications(toast) {
      await axios.post(`messages/mark-read/`)
    },



    // PUT requests
    async connectMerakiIntegration(organizationID, apiKey, toast) {
      let rb = {
        settings: [
          {
            "code": "MERAKI_ORGANIZATION_ID",
            "value": organizationID,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "MERAKI_API_KEY",
            "value": apiKey,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }
      rb = await signDictWithElevatedPermissions(rb, toast)
      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("Meraki integration successfully updated"), TOAST_SUCCESS_OPTIONS);
    },
    async connectMerakiChinaIntegration(organizationID, apiKey, toast) {
      let rb = {
        settings: [
          {
            "code": "MERAKI_CHINA_ORGANIZATION_ID",
            "value": organizationID,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "MERAKI_CHINA_API_KEY",
            "value": apiKey,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }
      rb = await signDictWithElevatedPermissions(rb, toast)
      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("Meraki China integration successfully updated"), TOAST_SUCCESS_OPTIONS);
    },
    async connectKPNIntegration(username, password, toast) {
      let rb = {
        settings: [
          {
            "code": "KPN_USERNAME",
            "value": username,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "KPN_PASSWORD",
            "value": password,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }
      rb = await signDictWithElevatedPermissions(rb, toast)

      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("KPN integration successfully updated"), TOAST_SUCCESS_OPTIONS);
    },
    async connectJAMFIntegration(username, password, toast) {
      let rb = {
        settings: [
          {
            "code": "JAMF_USERNAME",
            "value": username,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "JAMF_PASSWORD",
            "value": password,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }
      rb = await signDictWithElevatedPermissions(rb, toast)

      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("JAMF integration successfully updated"), TOAST_SUCCESS_OPTIONS);
    },
    async connectVerkadaIntegration(orgID, apiKey, cloudEnv, toast) {
      let rb = {
        settings: [
          {
            "code": "VERKADA_ORGANIZATION_ID",
            "value": orgID,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "VERKADA_API_KEY",
            "value": apiKey,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "VERKADA_CLOUD_ENVIRONMENT",
            "value": cloudEnv,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }
      rb = await signDictWithElevatedPermissions(rb, toast)

      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("Verkada integration successfully updated"), TOAST_SUCCESS_OPTIONS);
    },
    async connectWyebotIntegration(apiKey, cloudEnv, toast) {
      let rb = {
        settings: [
          {
            "code": "WYEBOT_API_KEY",
            "value": apiKey,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "WYEBOT_CLOUD_ENVIRONMENT",
            "value": cloudEnv,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }
      rb = await signDictWithElevatedPermissions(rb, toast)

      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("Wyebot integration successfully updated"), TOAST_SUCCESS_OPTIONS);
    },
    async connectMicrosoftIntegration(rb: ConnectMicrosoftIntegrationRequest, toast) {
      rb = await signDictWithElevatedPermissions(rb, toast)

      try {
        const { data } = await axios.put(`update-microsoft-settings/`, rb)
        this.microsoftSSOConfigured = true
        toast(toastSuccess("Microsoft integration successfully updated"), TOAST_SUCCESS_OPTIONS);
        return data
      } catch (e: any) {
        await this.getMicrosoftConfiguration()
        throw e
      }
    },


    async putLocationGranularPermissions(payload: any, toast: any) {
      payload = await signDictWithElevatedPermissions(payload, toast)

      const { data } = await axios.put('granular-permissions/locations/', payload)
      toast(toastSuccess("Changes saved successfully."), TOAST_SUCCESS_OPTIONS);
      return data as LocationGranularPermissions
    },


    // PATCH requests
    async setLocationGranularPermissions(value: boolean, toast: any) {
      const locationGranularPermissionsRequest: PatchLocationGranularPermissionsRequest = await signDictWithElevatedPermissions({
        locations_enabled: value
      }, toast)
      const { data } = await axios.patch('granular-permissions/', locationGranularPermissionsRequest)
      this.granularPermissionsConfig = data
      toast(toastSuccess(`${value ? 'Enabling' : 'Disabling'} granular permissions for this module was successful!`), TOAST_SUCCESS_OPTIONS);
      return this.granularPermissionsConfig as GranularPermissionsConfig
    },
    async modifyLocationNamingStrategy(locationNamingStrategyRequest: LocationNamingStrategyRequest, toast: any) {
      await axios.patch(`strategy/location-naming/`, locationNamingStrategyRequest)
      toast(toastSuccess("Location naming strategy was successfully changed!"), TOAST_SUCCESS_OPTIONS);
    },
    async modifyNetworkNamingStrategy(networkNamingStrategyRequest: NetworkNamingStrategyRequest, toast: any) {
      await axios.patch(`strategy/network-naming/`, networkNamingStrategyRequest)
      toast(toastSuccess("Network naming strategy was successfully changed!"), TOAST_SUCCESS_OPTIONS);
    },
    async changePassword(changePasswordRequest: ChangePasswordRequest, toast: any) {
      try {
        await axios.patch(`change-password/`, changePasswordRequest)
        const requestBody = {
          email: this.profile.email,
          password: changePasswordRequest.password,
          remember_me: false,
        }
        const AuthStore = useAuthStore()

        try {
          await AuthStore.login(requestBody, toast)
        } catch (e) {
          // password change already succeeded; the interceptor already
          // toasted the login failure, so just fall through to manual login
        }
        toast(toastSuccess("Password was successfully changed!"), TOAST_SUCCESS_OPTIONS);
        return
      } catch (e: any) {
        throw e
      }
    },
    async modifyMerakiSettings(patchMerakiSettingsRequest: PatchMerakiSettingsRequest, toast: any) {
      const { data } = await axios.patch(`meraki/settings/`, patchMerakiSettingsRequest)
      toast(toastSuccess("Meraki Settings successfully modified!"), TOAST_SUCCESS_OPTIONS);
      return data as MerakiSettings
    },
    async uploadLocationDocumentationByID(locationID: ID, documentation: string, name: string, toast: any) {
      if (!hasOrganizationModulePermissions(['LOCATION_DOCUMENTATION:CREATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      let documentationRequestBody = {
        name: name,
        documentation: documentation,
      }

      documentationRequestBody = await signDictWithElevatedPermissions(documentationRequestBody, toast)

      const { data } = await axios.post(`locations/${locationID}/documentation/`, documentationRequestBody, { hideToast: true })
      toast(toastSuccess("Documentation successfully added to location!"), TOAST_SUCCESS_OPTIONS);
      return data as LocationDocumentation
    },
     async uploadProcedureDocumentation(procedureName: string, documentation: string, filename: string, toast: any, procedureID?: ID){
       if (!hasOrganizationModulePermissions(['PROCEDURE_DOCUMENTATION:CREATE'])) {
         toast(toastError("Your organization does not have permission to perform this action!"));
         return
       }
       if(!hasUserPermissions(['@pollux.manage.documentation.procedures'])) {
         toast(toastError("You do not have permission to perform this action!"));
         return
       }
       let documentationRequestBody = {
         name: filename,
         procedure_name: procedureName,
         documentation: documentation,
       }
 
       documentationRequestBody = await signDictWithElevatedPermissions(documentationRequestBody, toast)

       let url = `procedures/documentation/`
       if (procedureID) {
         url = `procedures/${procedureID}/documentation/`
       }
       const {data} = await axios.post(url, documentationRequestBody, { hideToast: true })
       toast(toastSuccess("Procedure documentation successfully added!"), TOAST_SUCCESS_OPTIONS);
       return data as ProcedureDocumentation
    },
    async setUserRoles(userID: ID, patchUserRoles: PatchUserRoles, toast: any){
      if(!hasUserPermissions(['@pollux.manage.main.user.role'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      patchUserRoles = await signDictWithElevatedPermissions(patchUserRoles, toast)

      await axios.patch(`user-roles/${userID}/`, patchUserRoles)
      toast(toastSuccess("Roles successfully modified for user!"), TOAST_SUCCESS_OPTIONS);
    },
    async addUserToRole(userID: ID, roleID: ID, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.user.role'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      try {
        const { data } = await axios.get(`users/${userID}/`)
        const roles = data.roles.map(x => x.id)

        const newRoles = [...new Set([...roles, roleID])]

        let rb = {
          roles: newRoles,
        }

        rb = await signDictWithElevatedPermissions(rb, toast)

        await axios.patch(`user-roles/${userID}/`, rb)

        toast(toastSuccess("User successfully added to role!"), TOAST_SUCCESS_OPTIONS);
        return data as Location
      } catch (e: any) {
        throw e
      }
    },

    async removeUserFromRole(userID: ID, roleID: ID, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.user.role'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      try {
        const { data } = await axios.get(`users/${userID}/`)
        const roles = data.roles.map(x => x.id).filter(x => x != roleID)

        let rb = {
          roles,
        }

        rb = await signDictWithElevatedPermissions(rb, toast)

        await axios.patch(`user-roles/${userID}/`, rb)

        toast(toastSuccess("User successfully removed from role!"), TOAST_SUCCESS_OPTIONS);
        return data as Location
      } catch (e: any) {
        throw e
      }
    },
    async modifyLocationByID(id: ID, patchLocationRequest: PatchLocationRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.location.location'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['LOCATION:UPDATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const { data } = await axios.patch(`locations/${id}/`, patchLocationRequest)
      toast(toastSuccess("Location successfully modified!"), TOAST_SUCCESS_OPTIONS);
      return data as Location
    },
    async modifyLocationTypeByID(id: ID, patchLocationTypeRequest: PatchLocationTypeRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.location.type'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['LOCATION_TYPE:UPDATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const { data } = await axios.patch(`location-types/${id}/`, patchLocationTypeRequest)
      toast(toastSuccess("Location type successfully modified!"), TOAST_SUCCESS_OPTIONS);
      return data as LocationType
    },
    async modifyLocationBranchByID(id: ID, patchLocationBranchRequest: PatchLocationBranchRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.location.branch'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['LOCATION_BRANCH:UPDATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const { data } = await axios.patch(`location-branches/${id}/`, patchLocationBranchRequest)
      toast(toastSuccess("Location branch successfully modified!"), TOAST_SUCCESS_OPTIONS);
      return data as Branch
    },
    async modifyNetworkByID(id: ID, patchNetworkRequest: PatchNetworkRequest, toast: any, config: AxiosRequestConfig = {}) {
      if (!hasUserPermissions(['@pollux.manage.meraki.network'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN:UPDATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      try {
        patchNetworkRequest = await signDictWithElevatedPermissions(patchNetworkRequest, toast)
      } catch (e) {
        throw {
          response: {
            data: {
              error: ["Elevated permissions must be granted to perform this action"]
            }
          }
        }
      }


      const { data } = await axios.patch(`networks/${id}/`, patchNetworkRequest, config)
      toast(toastSuccess("Network successfully modified!"), TOAST_SUCCESS_OPTIONS);
      return data as Location
    },
    async modifyOrganization(patchOrganizationRequest: PatchOrganizationRequest, toast: any) {
      if (!hasAnyUserPermissions([['@pollux.manage.main.organization.branding'], ['@pollux.manage.main.organization.it_management']])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      const { data } = await axios.patch(`organization/`, patchOrganizationRequest)
      toast(toastSuccess("Organization successfully modified!"), TOAST_SUCCESS_OPTIONS);
      return data as Organization
    },
    async modifyOrganizationIntegrations(patchOrganizationIntegrationsRequest: PatchOrganizationIntegrationsRequest[], toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.organization.integration'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      const { data } = await axios.patch(`organization-settings/`, patchOrganizationIntegrationsRequest)
      toast(toastSuccess("Organization successfully modified!", TOAST_SUCCESS_OPTIONS));
      return data as OrganizationSetting[]
    },
    async modifyVLANRule(id: ID, patchVLANRuleRequest: VLANRuleRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.ipam_blueprint.vlan_rule'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['BLUEPRINTS:UPDATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const { data } = await axios.patch(`vlan-rules/${id}/`, patchVLANRuleRequest)
      toast(toastSuccess("VLAN rule successfully modified"), TOAST_SUCCESS_OPTIONS);
      return data as VLANRule
    },
    async modifyVLANRuleAction(id: ID, patchVLANRuleActionRequest: VLANRuleActionRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.ipam_blueprint.vlan_rule_action'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      if (!hasOrganizationModulePermissions(['BLUEPRINTS:UPDATE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const { data } = await axios.patch(`vlan-rule-actions/${id}/`, patchVLANRuleActionRequest)
      toast(toastSuccess("VLAN rule action successfully modified"), TOAST_SUCCESS_OPTIONS);
      return data as VLANRule
    },
    async modifyUserByID(id: ID, patchUserRequest: PatchUserRequest, toast: any, onboarding = false) {
      if (!onboarding) {
        if (!hasAnyUserPermissions([['@pollux.manage.main.user'], ['@pollux.manage.main.user.self'], ['@pollux.manage.main.role']])) {
          toast(toastError("You do not have permission to perform this action!"));
          return
        }

        patchUserRequest = await signDictWithElevatedPermissions(patchUserRequest, toast)
      }

      const { data } = await axios.patch(`users/${id}/`, patchUserRequest)
      toast(toastSuccess("User was successfully modified"), TOAST_SUCCESS_OPTIONS);
      return data as User
    },
    async modifyRoleByID(id: ID, patchRoleRequest: PatchRoleRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.role'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      const { data } = await axios.patch(`roles/${id}/`, patchRoleRequest)
      toast(toastSuccess("Role successfully modified"), TOAST_SUCCESS_OPTIONS);
      return data as Role
    },
    async modifySimCardNoteByID(id: ID, patchSimNoteRequest: PatchSimNoteRequest, toast: any) {
      if (!hasUserPermissions(['@pollux.manage.external_tools.sim_cards.notes'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      const { data } = await axios.patch(`sim-cards/note/${id}/`, patchSimNoteRequest)
      toast(toastSuccess("SIM note successfully modified"), TOAST_SUCCESS_OPTIONS);
      return data as Role
    },




    //DELETE requests
    async deleteSimCardNoteByID(id: ID, toast: any) {
      if (!hasUserPermissions(['@pollux.delete.external_tools.sim_cards.notes'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      await axios.delete(`sim-cards/note/${id}/`)
      toast(toastSuccess("SIM note successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteAPIKeyByID(id: ID, toast: any) {
      if (!hasUserPermissions(['@pollux.delete.main.devapikey'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      const params = await signWithElevatedPermissions(toast)

      await axios.delete(`api-keys/${id}/`, {
        params
      })
      toast(toastSuccess("API key successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteLocationDocumentationByID(id: ID, toast: any) {
      if (!hasOrganizationModulePermissions(['LOCATION_DOCUMENTATION:DELETE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const params = await signWithElevatedPermissions(toast)

      await axios.delete(`location-documentations/${id}/`, {
        params,
      })
      toast(toastSuccess("Location documentation successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteProcedureDocumentationByID(id: ID, toast: any){
      if (!hasOrganizationModulePermissions(['PROCEDURE_DOCUMENTATION:DELETE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }
      if (!hasUserPermissions(['@pollux.delete.documentation.procedures'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      const params = await signWithElevatedPermissions(toast)
      
      await axios.delete(`procedure-documentations/${id}/`, { params })
      toast(toastSuccess("Documentation successfully deleted!"), TOAST_SUCCESS_OPTIONS);
    },
    async deleteRoleByID(id: ID, toast: any){
      if(!hasUserPermissions(['@pollux.delete.main.role'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      const params = await signWithElevatedPermissions(toast)

      await axios.delete(`roles/${id}/`, {
        params
      })
      toast(toastSuccess("Role successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteUserByID(id: ID, toast: any) {
      if (!hasUserPermissions(['@pollux.delete.main.user'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      const params = await signWithElevatedPermissions(toast)

      await axios.delete(`users/${id}/`, {
        params,
      })
      toast(toastSuccess("User successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteLocationByID(id: ID, toast: any) {
      if (!hasOrganizationModulePermissions(['LOCATION:DELETE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const params = await signWithElevatedPermissions(toast)

      await axios.delete(`locations/${id}/`, {
        params
      })
      toast(toastSuccess("Location successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteLocationTypeByID(id: ID, toast: any) {
      if (!hasOrganizationModulePermissions(['LOCATION_TYPE:DELETE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      await axios.delete(`location-types/${id}/`)
      toast(toastSuccess("Location type successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteLocationBranchByID(id: ID, toast: any) {
      if (!hasOrganizationModulePermissions(['LOCATION_BRANCH:DELETE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      await axios.delete(`location-branches/${id}/`)
      toast(toastSuccess("Location branch successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteNetworkByID(id: ID, toast: any) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN:DELETE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      const params = await signWithElevatedPermissions(toast)

      await axios.delete(`networks/${id}/`, {
        params
      })
      toast(toastSuccess("Network successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteDeviceTypeByID(id: ID, toast: any) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN:DELETE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      await axios.delete(`device-types/${id}/`)
      toast(toastSuccess("Device type successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteDeviceByID(id: ID, toast: any) {
      if (!hasOrganizationModulePermissions(['MERAKI_MAIN:DELETE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }

      await axios.delete(`devices/${id}/`)
      toast(toastSuccess("Device successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteNotificationByID(id: ID) {
      await axios.delete(`messages/${id}/`)
      await this.getMessages()
    },
    async deleteMerakiIntegration(toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.organization.integration'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      let rb = {
        settings: [
          {
            "code": "MERAKI_ORGANIZATION_ID",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "MERAKI_API_KEY",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }

      rb = await signDictWithElevatedPermissions(rb, toast)

      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("Meraki integration successfully disconnected"), TOAST_SUCCESS_OPTIONS);
    },
    async deleteMerakiChinaIntegration(toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.organization.integration'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      let rb = {
        settings: [
          {
            "code": "MERAKI_CHINA_ORGANIZATION_ID",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "MERAKI_CHINA_API_KEY",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }

      rb = await signDictWithElevatedPermissions(rb, toast)

      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("Meraki China integration successfully disconnected"), TOAST_SUCCESS_OPTIONS);
    },
    async deleteKPNIntegration(toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.organization.integration'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      let rb = {
        settings: [
          {
            "code": "KPN_USERNAME",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "KPN_PASSWORD",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }

      rb = await signDictWithElevatedPermissions(rb, toast)

      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("KPN integration successfully disconnected"), TOAST_SUCCESS_OPTIONS);
    },
    async deleteJAMFIntegration(toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.organization.integration'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      let rb = {
        settings: [
          {
            "code": "JAMF_USERNAME",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "JAMF_PASSWORD",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }

      rb = await signDictWithElevatedPermissions(rb, toast)

      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("JAMF integration successfully disconnected"), TOAST_SUCCESS_OPTIONS);
    },
    async deleteVerkadaIntegration(toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.organization.integration'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      let rb = {
        settings: [
          {
            "code": "VERKADA_ORGANIZATION_ID",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "VERKADA_API_KEY",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "VERKADA_CLOUD_ENVIRONMENT",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }

      rb = await signDictWithElevatedPermissions(rb, toast)

      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("Verkada integration successfully disconnected"), TOAST_SUCCESS_OPTIONS);
    },
    async deleteWyebotIntegration(toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.organization.integration'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      let rb = {
        settings: [
          {
            "code": "WYEBOT_API_KEY",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          },
          {
            "code": "WYEBOT_CLOUD_ENVIRONMENT",
            "value": null,
            ...(await signWithElevatedPermissions(toast))
          }
        ]
      }

      rb = await signDictWithElevatedPermissions(rb, toast)

      await axios.patch(`organization-settings/`, rb)
      toast(toastSuccess("Wyebot integration successfully disconnected"), TOAST_SUCCESS_OPTIONS);
    },
    async deleteMicrosoftIntegration(toast: any) {
      if (!hasUserPermissions(['@pollux.manage.main.organization.integration'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }

      let rb = {
        "microsoft_tenant_id": null,
        "microsoft_client_id": null,
        "microsoft_client_secret": null
      }

      rb = await signDictWithElevatedPermissions(rb, toast)

      try {
        const { data } = await axios.put(`update-microsoft-settings/`, rb)
        this.microsoftSSOConfigured = false
        toast(toastSuccess("Microsoft integration successfully disconnected"), TOAST_SUCCESS_OPTIONS);
        return data
      } catch (e: any) {
        await this.getMicrosoftConfiguration()
        throw e
      }
    },
    async deleteInvitation(id: ID, toast: any) {
      const params = await signWithElevatedPermissions(toast)

      await axios.delete(`user-invitations/${id}/`, {
        params
      })
      toast(toastSuccess("Invitation successfully deleted"), TOAST_SUCCESS_OPTIONS);
      return true
    },
    async deleteAllLocationDocumentationByID(locationID: ID, toast: any) {
      if (!hasOrganizationModulePermissions(['LOCATION_DOCUMENTATION:DELETE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }
      const params = await signWithElevatedPermissions(toast)
      try {
        await axios.delete(`locations/${locationID}/documentation/`, {
          params
        })
        toast(toastSuccess("Documentation successfully deleted from location!"), TOAST_SUCCESS_OPTIONS);
      } catch (e: any) {
        throw e
      }
    },

    async deleteAllProcedureDocumentationByID(procedureID: ID, toast: any){
      if(!hasOrganizationModulePermissions(['PROCEDURE_DOCUMENTATION:DELETE'])) {
        toast(toastError("Your organization does not have permission to perform this action!"));
        return
      }
      if (!hasUserPermissions(['@pollux.delete.documentation.procedures'])) {
        toast(toastError("You do not have permission to perform this action!"));
        return
      }
      const params = await signWithElevatedPermissions(toast)
      try{
        await axios.delete(`procedures/${procedureID}/documentation/`, {
          params
        })
        toast(toastSuccess("Documentation successfully deleted from procedure!"), TOAST_SUCCESS_OPTIONS);
      }catch(e : any){
        throw e
      }
    },

    async deleteWyebotTestByID(testID: ID, toast: any){
      // TODO: Implement permissions
      const params = await signWithElevatedPermissions(toast)

      await axios.delete(`wyebot/tests/${testID}/`, {
        params
      })
      toast(toastSuccess("Wyebot test was successfully deleted!"), TOAST_SUCCESS_OPTIONS);
    }
  }
})
